import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle,
  Loader2,
  AlertTriangle,
  AlertCircle,
  Sparkles,
  Brain,
  ListChecks,
  Target,
  Users,
  Save,
  User,
  X,
  MapPin,
  Building2,
  ExternalLink,
  Award,
  ThumbsUp,
  ThumbsDown,
  Upload,
  FileText,
  Eye,
  Download,
  Clock,
  ChevronDown,
  ChevronUp,
  Pill,
  Hospital,
  Briefcase,
  Timer,
  ArrowUpDown,
  Send,
  RefreshCw,
  Plus,
  Mail,
  Copy,
  Link,
} from 'lucide-react';
import { clsx } from 'clsx';
import { formatDistanceToNow } from 'date-fns';
import { api, PatientInfo, Question, CaseDocument, getDocumentUrl, downloadDocument } from '../lib/api';
import { SWISS_CANTONS } from '@bettermatch/shared';
import { PostalCodeAutocomplete } from '../components/PostalCodeAutocomplete';
import { AuthenticatedImage } from '../components/AuthenticatedImage';
import { AuthenticatedPdfViewer } from '../components/AuthenticatedPdfViewer';

type WorkflowStep = 'input' | 'questionnaire' | 'answers' | 'indication' | 'criteria' | 'recommendations' | 'results';

// Extended doctor result with all details from search
interface DoctorSearchResult {
  doctorId: string;
  type: string;
  name: string;
  title: string;
  institution: string;
  city: string;
  canton: string;
  specialty: string;
  matchScore: number;
  matchReasons: string[];
  criteriaMatched: string[];
  expertise: string;
  website: string;
  source: string;
  // Contact info (if available)
  address?: string;
  phone?: string;
  email?: string;
  // ChatGPT specific
  pros?: string[];
  cons?: string[];
}

// Helper to convert File to base64
async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove the data URL prefix (e.g., "data:image/jpeg;base64,")
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Phase definitions for grouping steps (matching terminology document)
const PHASES = [
  { id: 1, name: 'Patient Data Intake', steps: ['input', 'questionnaire', 'answers'] },
  { id: 2, name: 'Case Analysis', steps: ['indication'] },
  { id: 3, name: 'Matching Criteria', steps: ['criteria'] },
  { id: 4, name: 'Matching to Care Provider', steps: ['recommendations', 'results'] },
];

// Full steps for standalone mode (no existing case)
// Roles: doctor, patient, or ai (AI on step means AI performs that step)
const STANDALONE_STEPS: { id: WorkflowStep; label: string; icon: React.ComponentType<{ className?: string }>; phase: number; role: 'doctor' | 'patient' | 'ai' }[] = [
  { id: 'input', label: 'Patient Data Intake', icon: User, phase: 1, role: 'doctor' },
  { id: 'questionnaire', label: 'Build Questionnaire', icon: ListChecks, phase: 1, role: 'ai' },
  { id: 'answers', label: 'Patient Answers', icon: Users, phase: 1, role: 'patient' },
  { id: 'indication', label: 'Case Analysis', icon: Brain, phase: 2, role: 'doctor' },
  { id: 'criteria', label: 'Matching Criteria', icon: Target, phase: 3, role: 'doctor' },
  { id: 'recommendations', label: 'Matching to Care Provider', icon: Users, phase: 4, role: 'doctor' },
  { id: 'results', label: 'Save Case', icon: Save, phase: 4, role: 'doctor' },
];

// Reduced steps for existing case mode (skip input, results changes to "Complete")
const EXISTING_CASE_STEPS: { id: WorkflowStep; label: string; icon: React.ComponentType<{ className?: string }>; phase: number; role: 'doctor' | 'patient' | 'ai' }[] = [
  { id: 'questionnaire', label: 'Build Questionnaire', icon: ListChecks, phase: 1, role: 'ai' },
  { id: 'answers', label: 'Patient Answers', icon: Users, phase: 1, role: 'patient' },
  { id: 'indication', label: 'Case Analysis', icon: Brain, phase: 2, role: 'doctor' },
  { id: 'criteria', label: 'Matching Criteria', icon: Target, phase: 3, role: 'doctor' },
  { id: 'recommendations', label: 'Matching to Care Provider', icon: Users, phase: 4, role: 'doctor' },
  { id: 'results', label: 'Complete', icon: Check, phase: 4, role: 'doctor' },
];

export function AndyProWorkflow() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id: caseId } = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();
  const urlExecutionId = searchParams.get('executionId');
  const autostart = searchParams.get('autostart') === 'true';

  // Determine if we're in existing case mode or standalone mode
  const isExistingCase = !!caseId;
  const STEPS = isExistingCase ? EXISTING_CASE_STEPS : STANDALONE_STEPS;

  // Step state - start at questionnaire for existing case, input for standalone
  const [currentStep, setCurrentStep] = useState<WorkflowStep>(isExistingCase ? 'questionnaire' : 'input');
  const [initialized, setInitialized] = useState(false);

  // Patient/case data
  const [condition, setCondition] = useState('');
  const [patientInfo, setPatientInfo] = useState<PatientInfo>({
    age: undefined,
    gender: undefined,
    canton: undefined,
    postalCode: undefined,
  });
  const [caseTitle, setCaseTitle] = useState('');
  const [existingCaseTitle, setExistingCaseTitle] = useState('');

  // Workflow data
  const [questions, setQuestions] = useState<Question[]>([]);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [indication, setIndication] = useState<Record<string, unknown> | null>(null);
  const [criteria, setCriteria] = useState<Record<string, unknown> | null>(null);
  const [specialistRole, setSpecialistRole] = useState('');

  // Recommendations from Gemini search
  const [recommendations, setRecommendations] = useState<{
    claude: DoctorSearchResult[];
    gemini: DoctorSearchResult[];
    chatgpt: DoctorSearchResult[];
  }>({
    claude: [],
    gemini: [],
    chatgpt: [],
  });
  const [activeRecommendationTab, setActiveRecommendationTab] = useState<'claude' | 'gemini' | 'chatgpt'>('gemini');
  const [selectedDoctor, setSelectedDoctor] = useState<DoctorSearchResult | null>(null);
  // Doctor sorting: match (default), name, city
  const [doctorSortBy, setDoctorSortBy] = useState<'match' | 'name' | 'city'>('match');

  // Track search errors for each source (to distinguish "no results" from "error")
  type SearchErrorState = {
    hasError: boolean;
    errorType?: 'api_error' | 'parse_error' | 'network_error';
    message?: string;
  };
  const [searchErrors, setSearchErrors] = useState<{
    claude: SearchErrorState;
    gemini: SearchErrorState;
    chatgpt: SearchErrorState;
  }>({
    claude: { hasError: false },
    gemini: { hasError: false },
    chatgpt: { hasError: false },
  });

  // Saved case ID and execution ID
  const [savedCaseId, setSavedCaseId] = useState<string | null>(null);
  const [executionId, setExecutionId] = useState<string | null>(null);

  // Track completed steps for partial saves
  const [completedSteps, setCompletedSteps] = useState<string[]>([]);

  // Track last saved steps to avoid redundant auto-saves
  const lastSavedStepsRef = useRef<string[]>([]);

  // Document uploads for standalone mode
  const [uploadedFiles, setUploadedFiles] = useState<Array<{ file: File; id: string }>>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Track which files have been uploaded to the server (by their id)
  const uploadedFileIdsRef = useRef<Set<string>>(new Set());

  // Document preview for existing case documents
  const [previewDocument, setPreviewDocument] = useState<CaseDocument | null>(null);
  // Preview for locally uploaded files (not yet saved to server)
  const [previewLocalFile, setPreviewLocalFile] = useState<{ file: File; id: string } | null>(null);

  // Track disabled criteria (by index key like "critical-0", "high-1", etc.)
  const [disabledCriteria, setDisabledCriteria] = useState<Set<string>>(new Set());
  // Track manually added criteria (by their unique ID for deletion)
  const [manualCriteria, setManualCriteria] = useState<Set<string>>(new Set());
  // Add criteria modal state (null = closed, string = priority level to add to)
  const [addCriteriaModal, setAddCriteriaModal] = useState<{ priority: string; name: string; description: string } | null>(null);
  // Delete criteria confirmation modal state
  const [deleteCriteriaModal, setDeleteCriteriaModal] = useState<{ importance: string; index: number; name: string; manualId: string } | null>(null);

  // Analysis step tab navigation
  const [activeAnalysisTab, setActiveAnalysisTab] = useState<'summary' | 'assessment' | 'expectations' | 'documents'>('summary');
  // Timestamp for when analysis was completed
  const [analysisTimestamp, setAnalysisTimestamp] = useState<Date | null>(null);
  // Track whether warning is expanded
  const [warningExpanded, setWarningExpanded] = useState(false);
  // Track whether all document findings are shown
  const [showAllFindings, setShowAllFindings] = useState(false);
  // Track last saved timestamp
  const [lastSavedTime, setLastSavedTime] = useState<Date | null>(null);

  // Send to Patient modal state
  const [showSendToPatientModal, setShowSendToPatientModal] = useState(false);
  const [patientEmail, setPatientEmail] = useState('');
  const [patientMessage, setPatientMessage] = useState('');
  const [sendingToPatient, setSendingToPatient] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  // Fetch existing case data if in existing case mode
  const existingCaseQuery = useQuery({
    queryKey: ['case', caseId],
    queryFn: () => api.cases.get(caseId!),
    enabled: isExistingCase,
    staleTime: 0, // Always consider data stale to get latest recommendations
    refetchOnMount: 'always', // Refetch when component mounts
  });

  // Create execution mutation for new executions
  const createExecutionMutation = useMutation({
    mutationFn: (targetCaseId: string) => api.executions.create(targetCaseId),
    onSuccess: (result, targetCaseId) => {
      setExecutionId(result.data.id);
      // Invalidate queries so case list and detail pages show the new execution
      queryClient.invalidateQueries({ queryKey: ['cases'] });
      queryClient.invalidateQueries({ queryKey: ['case', targetCaseId] });
    },
  });

  // Initialize data from existing case
  useEffect(() => {
    if (isExistingCase && existingCaseQuery.data && !initialized) {
      const caseData = existingCaseQuery.data.data;
      setCondition(caseData.condition || '');
      setExistingCaseTitle(caseData.title || '');

      if (caseData.patientInfo) {
        setPatientInfo({
          age: caseData.patientInfo.age,
          gender: caseData.patientInfo.gender,
          canton: caseData.patientInfo.canton,
          postalCode: caseData.patientInfo.postalCode,
        });
      }

      // Only resume if explicitly requested via URL param
      if (urlExecutionId) {
        // Find specific execution from URL to resume
        const executionToResume = caseData.executions?.find(e => e.id === urlExecutionId);

        if (executionToResume) {
          setExecutionId(executionToResume.id);

          // Load completed steps and initialize the saved steps ref to avoid re-saving
          if (executionToResume.completedSteps && Array.isArray(executionToResume.completedSteps)) {
            setCompletedSteps(executionToResume.completedSteps);
            lastSavedStepsRef.current = [...executionToResume.completedSteps];
          }

          // Load questionnaire if exists
          if (executionToResume.questionnaire && executionToResume.questionnaire.length > 0) {
            setQuestions(executionToResume.questionnaire);
            setAnswers(executionToResume.answers || {});
          }

          // Load indication if exists
          if (executionToResume.indication && Object.keys(executionToResume.indication).length > 0) {
            setIndication(executionToResume.indication);
          }

          // Load criteria if exists
          if (executionToResume.criteria && Object.keys(executionToResume.criteria).length > 0) {
            setCriteria(executionToResume.criteria);
            // Restore manual criteria tracking from loaded data
            const criteriaData = executionToResume.criteria as Record<string, unknown>;
            const criteriaList = (criteriaData.criteria || criteriaData.search_criteria || criteriaData.matching_criteria || []) as Array<Record<string, unknown>>;
            const manualIds = criteriaList
              .filter(c => c._manualId)
              .map(c => c._manualId as string);
            if (manualIds.length > 0) {
              setManualCriteria(new Set(manualIds));
            }
            // Restore disabled criteria state from loaded data
            const savedDisabledKeys = criteriaData.disabledCriteriaKeys as string[] | undefined;
            if (savedDisabledKeys && savedDisabledKeys.length > 0) {
              setDisabledCriteria(new Set(savedDisabledKeys));
              console.log('[AndyPro] Restored disabled criteria:', savedDisabledKeys);
            }
          }

          // Load recommendations if exists
          if (executionToResume.recommendations && executionToResume.recommendations.length > 0) {
            const loadedRecs = { claude: [] as DoctorSearchResult[], gemini: [] as DoctorSearchResult[], chatgpt: [] as DoctorSearchResult[] };
            for (const rec of executionToResume.recommendations) {
              // Transform saved DoctorRecommendation to DoctorSearchResult format
              const rawDoctors = (rec.doctors as Array<{
                doctorId: string;
                doctorName: string;
                specialty: string;
                score: number;
                reasoning: string;
                matchedCriteria: string[];
                title?: string;
                institution?: string;
                city?: string;
                canton?: string;
                expertise?: string;
                website?: string;
              }>) || [];
              // Map recommendation source to display source
              const displaySource = rec.source === 'CLAUDE' ? 'claude_web_search'
                : rec.source === 'CHATGPT' ? 'chatgpt_web_search'
                : 'gemini_web_search';
              const doctors: DoctorSearchResult[] = rawDoctors.map(d => {
                // Split reasoning carefully - only on ". " followed by uppercase letter (new sentence)
                // This avoids breaking on "Prof. " or "Dr. " or "M. "
                const matchReasons = d.reasoning
                  ? d.reasoning.split(/\.\s+(?=[A-Z])/).map(s => s.trim()).filter(Boolean)
                  : [];
                return {
                  doctorId: d.doctorId,
                  type: 'doctor',
                  name: d.doctorName,
                  title: d.title || '',
                  institution: d.institution || '',
                  city: d.city || '',
                  canton: d.canton || '',
                  specialty: d.specialty,
                  matchScore: d.score,
                  matchReasons,
                  criteriaMatched: d.matchedCriteria || [],
                  expertise: d.expertise || '',
                  website: d.website || '',
                  source: displaySource,
                };
              });
              if (rec.source === 'CLAUDE') {
                loadedRecs.claude = doctors;
              } else if (rec.source === 'CHATGPT') {
                loadedRecs.chatgpt = doctors;
              } else if (rec.source === 'GEMINI') {
                loadedRecs.gemini = doctors;
              }
            }
            setRecommendations(loadedRecs);
          }

          // Restore current step from saved state
          // But if we have questions and are at 'questionnaire' step, advance to 'answers'
          if (executionToResume.currentStep) {
            const savedStep = executionToResume.currentStep as WorkflowStep;
            // If saved at questionnaire but we have questions, skip to answers
            if (savedStep === 'questionnaire' && executionToResume.questionnaire?.length > 0) {
              setCurrentStep('answers');
            } else {
              setCurrentStep(savedStep);
            }
          } else if (executionToResume.questionnaire?.length > 0) {
            // No saved step but we have questions - go to answers
            setCurrentStep('answers');
          }
        }
      } else {
        // No executionId in URL - check if there's an existing IN_PROGRESS execution to resume
        const existingInProgressExecution = caseData.executions?.find(
          (e) => e.status === 'IN_PROGRESS'
        );

        if (existingInProgressExecution) {
          // Resume the existing IN_PROGRESS execution instead of creating a new one
          setExecutionId(existingInProgressExecution.id);

          // Load its data
          if (existingInProgressExecution.completedSteps && Array.isArray(existingInProgressExecution.completedSteps)) {
            setCompletedSteps(existingInProgressExecution.completedSteps);
            lastSavedStepsRef.current = [...existingInProgressExecution.completedSteps];
          }
          if (existingInProgressExecution.questionnaire && existingInProgressExecution.questionnaire.length > 0) {
            setQuestions(existingInProgressExecution.questionnaire);
            setAnswers(existingInProgressExecution.answers || {});
          }
          if (existingInProgressExecution.indication && Object.keys(existingInProgressExecution.indication).length > 0) {
            setIndication(existingInProgressExecution.indication);
          }
          if (existingInProgressExecution.criteria && Object.keys(existingInProgressExecution.criteria).length > 0) {
            setCriteria(existingInProgressExecution.criteria);
            // Restore manual and disabled criteria from loaded data
            const criteriaData = existingInProgressExecution.criteria as Record<string, unknown>;
            const criteriaList = (criteriaData.criteria || criteriaData.search_criteria || criteriaData.matching_criteria || []) as Array<Record<string, unknown>>;
            const manualIds = criteriaList.filter(c => c._manualId).map(c => c._manualId as string);
            if (manualIds.length > 0) {
              setManualCriteria(new Set(manualIds));
            }
            const savedDisabledKeys = criteriaData.disabledCriteriaKeys as string[] | undefined;
            if (savedDisabledKeys && savedDisabledKeys.length > 0) {
              setDisabledCriteria(new Set(savedDisabledKeys));
              console.log('[AndyPro] Restored disabled criteria:', savedDisabledKeys);
            }
          }
          // Set current step
          if (existingInProgressExecution.currentStep) {
            const savedStep = existingInProgressExecution.currentStep as WorkflowStep;
            if (savedStep === 'questionnaire' && existingInProgressExecution.questionnaire?.length > 0) {
              setCurrentStep('answers');
            } else {
              setCurrentStep(savedStep);
            }
          } else if (existingInProgressExecution.questionnaire?.length > 0) {
            setCurrentStep('answers');
          }
        } else {
          // No IN_PROGRESS execution exists - create a new one
          createExecutionMutation.mutate(caseId!);
        }
      }

      setInitialized(true);
    }
  }, [isExistingCase, existingCaseQuery.data, initialized, urlExecutionId, caseId]);

  // Generate sample case mutation (for AI Fill Form)
  const generateSampleCaseMutation = useMutation({
    mutationFn: () => api.andyPro.generateSampleCase(),
    onSuccess: (data) => {
      if (data.success && data.data) {
        setCondition(data.data.condition);
        setPatientInfo({
          age: data.data.patientInfo.age,
          gender: data.data.patientInfo.gender,
          canton: data.data.patientInfo.canton,
          postalCode: data.data.patientInfo.postalCode,
        });
        setCaseTitle(data.data.suggestedTitle);
      }
    },
  });

  // Generate questionnaire mutation
  const generateQuestionnaireMutation = useMutation({
    mutationFn: () =>
      api.andyPro.generateQuestionnaire({
        condition,
        patientInfo: {
          age: patientInfo.age,
          gender: patientInfo.gender,
          canton: patientInfo.canton,
        },
      }),
    onSuccess: (data) => {
      setQuestions(data.data.questions);
      setSpecialistRole(data.data.specialist_role || '');
      setCompletedSteps(prev => prev.includes('questionnaire') ? prev : [...prev, 'questionnaire']);
      setCurrentStep('answers');
    },
  });

  // Track if autostart has been triggered to avoid double-triggering
  const autostartTriggeredRef = useRef(false);

  // Auto-trigger questionnaire generation when autostart=true and execution is ready
  useEffect(() => {
    if (
      autostart &&
      isExistingCase &&
      initialized &&
      executionId &&
      condition &&
      questions.length === 0 &&
      !generateQuestionnaireMutation.isPending &&
      !autostartTriggeredRef.current
    ) {
      autostartTriggeredRef.current = true;
      generateQuestionnaireMutation.mutate();
    }
  }, [autostart, isExistingCase, initialized, executionId, condition, questions.length, generateQuestionnaireMutation]);

  // Autofill mutation
  const autofillMutation = useMutation({
    mutationFn: () =>
      api.andyPro.autofill({
        condition,
        patientInfo: {
          age: patientInfo.age,
          gender: patientInfo.gender,
          canton: patientInfo.canton,
        },
        questions: questions.map((q) => ({
          id: q.id,
          question: q.question,
          type: q.type,
          options: q.options,
        })),
      }),
    onSuccess: (data) => {
      // API returns { success: true, answers: { questionId: answer } }
      if (data.answers) {
        setAnswers(data.answers);
      }
    },
  });

  // Indication analysis mutation
  const indicationMutation = useMutation({
    mutationFn: async () => {
      const questionnaireData = questions.map((q) => ({
        id: q.id,
        question: q.question,
        answer: answers[q.id] || '',
      }));

      // Build documents array from existing case docs or uploaded files
      const documents: Array<{ filename?: string; base64Data?: string; originalName: string; mimeType: string }> = [];

      // Add existing case documents (they have filenames in storage)
      if (isExistingCase && existingCaseQuery.data?.data?.documents) {
        for (const doc of existingCaseQuery.data.data.documents) {
          // Only include images and PDFs
          if (doc.mimeType.startsWith('image/') || doc.mimeType === 'application/pdf') {
            documents.push({
              filename: doc.filename,
              originalName: doc.originalName,
              mimeType: doc.mimeType,
            });
          }
        }
      }

      // Add uploaded files (convert to base64)
      if (uploadedFiles.length > 0) {
        for (const { file } of uploadedFiles) {
          // Only include images and PDFs
          if (file.type.startsWith('image/') || file.type === 'application/pdf') {
            const base64Data = await fileToBase64(file);
            documents.push({
              base64Data,
              originalName: file.name,
              mimeType: file.type,
            });
          }
        }
      }

      console.log(`[Indication] Sending ${documents.length} documents for analysis`);

      return api.andyPro.analyzeIndication({
        condition,
        patientInfo: {
          age: patientInfo.age,
          gender: patientInfo.gender,
          canton: patientInfo.canton,
        },
        questionnaire: questionnaireData,
        documents: documents.length > 0 ? documents : undefined,
      });
    },
    onSuccess: (data) => {
      setIndication(data.data);
      setAnalysisTimestamp(new Date());
      // Reset tab to summary when new analysis comes in
      setActiveAnalysisTab('summary');
      // Mark indication as completed (answers already marked in handleNext)
      setCompletedSteps(prev => prev.includes('indication') ? prev : [...prev, 'indication']);
      // Stay on indication step to show results - user clicks Continue to proceed
    },
  });

  // Criteria generation mutation
  const criteriaMutation = useMutation({
    mutationFn: () => {
      const questionnaireData = questions.map((q) => ({
        id: q.id,
        question: q.question,
        answer: answers[q.id] || '',
      }));
      return api.andyPro.generateCriteria({
        condition,
        patientInfo: {
          age: patientInfo.age,
          gender: patientInfo.gender,
          canton: patientInfo.canton,
        },
        questionnaire: questionnaireData,
        indication: indication || {},
      });
    },
    onSuccess: (data) => {
      setCriteria(data.data);
      setCompletedSteps(prev => prev.includes('criteria') ? prev : [...prev, 'criteria']);
      // Stay on criteria step to show results - user clicks Continue to proceed
    },
  });

  // Helper to build search data
  const buildSearchData = () => {
    const criteriaData = criteria as {
      criteria?: Array<{ name: string; importance: string; description?: string; _manualId?: string }>;
      search_parameters?: { specialties?: string[]; max_distance_km?: number; min_experience_years?: number };
    };
    const searchParams = criteriaData?.search_parameters || {};
    const criteriaList = criteriaData?.criteria || [];

    // Filter out disabled criteria - only pass enabled criteria to search
    // Criteria key structure: "importance-index" (e.g., "critical-0", "high-1")
    const enabledCriteria: typeof criteriaList = [];

    // Track index per importance level to match checkbox keys
    const indexByImportance: Record<string, number> = {};

    criteriaList.forEach((c) => {
      const importance = (c.importance || 'medium').toLowerCase();
      const idx = indexByImportance[importance] ?? 0;
      const criteriaKey = `${importance}-${idx}`;

      // Only include if not disabled
      if (!disabledCriteria.has(criteriaKey)) {
        enabledCriteria.push(c);
      }

      indexByImportance[importance] = idx + 1;
    });

    console.log(`[AndyPro] buildSearchData: ${enabledCriteria.length}/${criteriaList.length} criteria enabled (${disabledCriteria.size} disabled)`);

    return {
      condition,
      specialty: searchParams.specialties?.[0],
      criteria: enabledCriteria,
      patientInfo: {
        age: patientInfo.age,
        gender: patientInfo.gender,
        canton: patientInfo.canton,
        postalCode: patientInfo.postalCode,
        cityName: patientInfo.cityName,
      },
      searchParameters: searchParams,
    };
  };

  // Helper to save recommendations to the database
  const saveRecommendationsToDb = async (
    source: 'CLAUDE' | 'GEMINI' | 'CHATGPT',
    doctors: DoctorSearchResult[]
  ) => {
    const targetCaseId = caseId || savedCaseId;
    if (!targetCaseId || !executionId || doctors.length === 0) return;

    try {
      await api.executions.saveRecommendations(targetCaseId, executionId, {
        source,
        doctors: doctors.map(d => ({
          doctorId: d.doctorId,
          doctorName: d.name,
          specialty: d.specialty,
          score: d.matchScore,
          reasoning: (d.matchReasons || []).join('. '),
          matchedCriteria: d.criteriaMatched || [],
          // Save additional fields for full display on reload
          title: d.title || '',
          institution: d.institution || '',
          city: d.city || '',
          canton: d.canton || '',
          expertise: d.expertise || '',
          website: d.website || '',
        })),
      });
      console.log(`[AndyPro] Saved ${doctors.length} ${source} recommendations to database`);
    } catch (error) {
      console.error(`[AndyPro] Failed to save ${source} recommendations:`, error);
    }
  };

  // Save search results to case execution (full raw results for analysis)
  const saveSearchResultsToExecution = async (
    source: 'claude' | 'gemini' | 'chatgpt',
    results: DoctorSearchResult[],
    metadata?: { recommendationId?: string; specialty?: string }
  ) => {
    const targetCaseId = caseId || savedCaseId;
    if (!targetCaseId || !executionId) {
      console.log('[AndyPro] Cannot save search results - missing caseId or executionId');
      return;
    }

    try {
      // Get current search results to merge with new results
      const currentExecution = await api.executions.get(targetCaseId, executionId);
      const existingResults = ((currentExecution?.data as any)?.searchResults as Record<string, unknown>) || {};

      // Merge with new results
      const updatedSearchResults = {
        ...existingResults,
        [source]: results,
        searchedAt: new Date().toISOString(),
        ...(metadata?.specialty && { specialty: metadata.specialty }),
        ...(metadata?.recommendationId && { recommendationId: metadata.recommendationId }),
      };

      await api.executions.update(targetCaseId, executionId, {
        searchResults: updatedSearchResults,
      } as any);
      console.log(`[AndyPro] Saved ${source} search results to execution`);
    } catch (error) {
      console.error(`[AndyPro] Failed to save ${source} search results:`, error);
    }
  };

  // Gemini search mutation - runs independently
  // --- Search mutations for all 3 providers ---

  const claudeSearchMutation = useMutation({
    mutationFn: async () => {
      console.log('[AndyPro] Starting Claude search...');
      const searchData = buildSearchData();
      const result = await api.andyPro.searchClaude(searchData);
      if (result.success) {
        return {
          results: result.webResults.map(r => ({
            ...r,
            criteriaMatched: r.criteriaMatched || [],
          })) as DoctorSearchResult[],
          hasError: false,
          errorType: undefined,
          message: undefined
        };
      }
      throw new Error((result as { error?: string }).error || 'Claude search failed');
    },
    onSuccess: (data) => {
      console.log('[AndyPro] Claude search returned', data.results.length, 'results');
      setRecommendations(prev => ({ ...prev, claude: data.results }));
      if (data.hasError) {
        setSearchErrors(prev => ({ ...prev, claude: { hasError: true, errorType: data.errorType, message: data.message } }));
      } else {
        setSearchErrors(prev => ({ ...prev, claude: { hasError: false } }));
        if (data.results.length > 0) saveRecommendationsToDb('CLAUDE', data.results);
        saveSearchResultsToExecution('claude', data.results);
      }
    },
    onError: (error) => {
      console.error('[AndyPro] Claude search failed:', error);
      setSearchErrors(prev => ({ ...prev, claude: { hasError: true, errorType: 'api_error', message: error instanceof Error ? error.message : 'Search request failed' } }));
    },
  });

  const geminiSearchMutation = useMutation({
    mutationFn: async () => {
      console.log('[AndyPro] Starting Gemini search...');
      const searchData = buildSearchData();
      const result = await api.andyPro.searchGemini(searchData);
      if (result.success) {
        return {
          results: result.webResults.map(r => ({
            ...r,
            criteriaMatched: r.criteriaMatched || [],
          })) as DoctorSearchResult[],
          hasError: false,
          errorType: undefined,
          message: undefined
        };
      }
      throw new Error((result as { error?: string }).error || 'Gemini search failed');
    },
    onSuccess: (data) => {
      console.log('[AndyPro] Gemini search returned', data.results.length, 'results');
      setRecommendations(prev => ({ ...prev, gemini: data.results }));
      if (data.hasError) {
        setSearchErrors(prev => ({ ...prev, gemini: { hasError: true, errorType: data.errorType, message: data.message } }));
      } else {
        setSearchErrors(prev => ({ ...prev, gemini: { hasError: false } }));
        if (data.results.length > 0) saveRecommendationsToDb('GEMINI', data.results);
        saveSearchResultsToExecution('gemini', data.results);
      }
    },
    onError: (error) => {
      console.error('[AndyPro] Gemini search failed:', error);
      setSearchErrors(prev => ({ ...prev, gemini: { hasError: true, errorType: 'api_error', message: error instanceof Error ? error.message : 'Search request failed' } }));
    },
  });

  const chatgptSearchMutation = useMutation({
    mutationFn: async () => {
      console.log('[AndyPro] Starting ChatGPT search...');
      const searchData = buildSearchData();
      const result = await api.andyPro.searchChatgpt(searchData);
      if (result.success) {
        return {
          results: result.webResults.map(r => ({
            ...r,
            criteriaMatched: r.criteriaMatched || [],
          })) as DoctorSearchResult[],
          hasError: false,
          errorType: undefined,
          message: undefined
        };
      }
      throw new Error((result as { error?: string }).error || 'ChatGPT search failed');
    },
    onSuccess: (data) => {
      console.log('[AndyPro] ChatGPT search returned', data.results.length, 'results');
      setRecommendations(prev => ({ ...prev, chatgpt: data.results }));
      if (data.hasError) {
        setSearchErrors(prev => ({ ...prev, chatgpt: { hasError: true, errorType: data.errorType, message: data.message } }));
      } else {
        setSearchErrors(prev => ({ ...prev, chatgpt: { hasError: false } }));
        if (data.results.length > 0) saveRecommendationsToDb('CHATGPT', data.results);
        saveSearchResultsToExecution('chatgpt', data.results);
      }
    },
    onError: (error) => {
      console.error('[AndyPro] ChatGPT search failed:', error);
      setSearchErrors(prev => ({ ...prev, chatgpt: { hasError: true, errorType: 'api_error', message: error instanceof Error ? error.message : 'Search request failed' } }));
    },
  });

  // Start all 3 searches in parallel
  const startAllSearches = () => {
    console.log('[AndyPro] Starting all doctor searches (Claude, Gemini, ChatGPT)...');
    setRecommendations({ claude: [], gemini: [], chatgpt: [] });
    setSearchErrors({ claude: { hasError: false }, gemini: { hasError: false }, chatgpt: { hasError: false } });
    claudeSearchMutation.mutate();
    geminiSearchMutation.mutate();
    chatgptSearchMutation.mutate();
  };

  // Check if any search is still pending
  const isAnySearchPending = claudeSearchMutation.isPending || geminiSearchMutation.isPending || chatgptSearchMutation.isPending;

  // Track when all searches complete
  const allSearchesComplete = !isAnySearchPending && (claudeSearchMutation.isSuccess || geminiSearchMutation.isSuccess || chatgptSearchMutation.isSuccess);

  // Mark recommendations as completed when all searches are done
  useEffect(() => {
    if (allSearchesComplete && currentStep === 'recommendations') {
      const totalResults = recommendations.claude.length + recommendations.gemini.length + recommendations.chatgpt.length;
      if (totalResults > 0 && !completedSteps.includes('recommendations')) {
        setCompletedSteps(prev => [...prev, 'recommendations']);
      }
    }
  }, [allSearchesComplete, currentStep, recommendations, completedSteps]);

  // Auto-save whenever completedSteps changes (for existing cases with an execution)
  useEffect(() => {
    // Only auto-save for existing cases with an execution ID
    if (!executionId || (!isExistingCase && !savedCaseId)) return;
    if (saveCaseMutation.isPending) return;

    // Check if we have new completed steps that haven't been saved yet
    const newSteps = completedSteps.filter(step => !lastSavedStepsRef.current.includes(step));
    if (newSteps.length === 0) return;

    console.log('[Auto-save] New steps completed:', newSteps, '- triggering save');
    lastSavedStepsRef.current = [...completedSteps];
    saveCaseMutation.mutate({ markComplete: false });
  }, [completedSteps, executionId, isExistingCase, savedCaseId]);

  // Keyboard shortcuts: Cmd/Ctrl+Enter (next), Escape (back), Cmd/Ctrl+S (save)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const modKey = isMac ? e.metaKey : e.ctrlKey;

      // Cmd/Ctrl + Enter: Proceed to next step
      if (modKey && e.key === 'Enter') {
        e.preventDefault();
        // Trigger next if not on results step and not loading
        if (currentStep !== 'results') {
          // We'll let handleNext be called from the button - but we can simulate click
          const nextBtn = document.querySelector('[data-action="next"]') as HTMLButtonElement;
          if (nextBtn && !nextBtn.disabled) {
            nextBtn.click();
          }
        }
      }

      // Escape: Go back
      if (e.key === 'Escape') {
        e.preventDefault();
        const currentStepIndex = STEPS.findIndex((s) => s.id === currentStep);
        if (currentStepIndex > 0) {
          const backBtn = document.querySelector('[data-action="back"]') as HTMLButtonElement;
          if (backBtn && !backBtn.disabled) {
            backBtn.click();
          }
        }
      }

      // Cmd/Ctrl + S: Save progress
      if (modKey && e.key === 's') {
        e.preventDefault();
        if (currentStep !== 'input' && currentStep !== 'results') {
          const saveBtn = document.querySelector('[data-action="save"]') as HTMLButtonElement;
          if (saveBtn && !saveBtn.disabled) {
            saveBtn.click();
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentStep, STEPS]);

  // Save/update case and execution mutation - now supports partial saves
  const saveCaseMutation = useMutation({
    mutationFn: async (options?: { markComplete?: boolean }) => {
      let targetCaseId = caseId;
      let targetExecutionId = executionId;

      console.log('[Save] Starting save...', {
        isExistingCase,
        caseId,
        savedCaseId,
        executionId,
        currentStep,
        questionsCount: questions.length,
        completedSteps,
      });

      if (!isExistingCase && !savedCaseId) {
        // Create new case (standalone mode)
        console.log('[Save] Creating new case...');
        const result = await api.cases.create({
          title: caseTitle || `${specialistRole || 'Medical'} Case - ${new Date().toLocaleDateString()}`,
          condition,
          patientInfo,
        });
        targetCaseId = result.data.id;
        console.log('[Save] Created case:', targetCaseId);
      } else if (savedCaseId) {
        targetCaseId = savedCaseId;
      }

      // Upload any documents that haven't been uploaded yet (standalone mode)
      if (!isExistingCase && targetCaseId && uploadedFiles.length > 0) {
        const filesToUpload = uploadedFiles.filter(f => !uploadedFileIdsRef.current.has(f.id));
        if (filesToUpload.length > 0) {
          console.log('[Save] Uploading', filesToUpload.length, 'new documents...');
          await api.cases.uploadDocuments(targetCaseId, filesToUpload.map(f => f.file));
          // Mark these files as uploaded
          filesToUpload.forEach(f => uploadedFileIdsRef.current.add(f.id));
          console.log('[Save] Documents uploaded successfully');
        }
      }

      // Create execution if we don't have one
      if (!targetExecutionId && targetCaseId) {
        console.log('[Save] Creating new execution for case:', targetCaseId);
        const execResult = await api.executions.create(targetCaseId);
        targetExecutionId = execResult.data.id;
        setExecutionId(targetExecutionId);
        console.log('[Save] Created execution:', targetExecutionId);
      }

      // Update execution with all workflow data and progress tracking
      if (targetCaseId && targetExecutionId) {
        // Determine status: COMPLETED if explicitly requested OR if all workflow steps are done
        const allStepsComplete = ['questionnaire', 'answers', 'indication', 'criteria', 'recommendations'].every(
          step => completedSteps.includes(step)
        );
        const isCompleted = options?.markComplete || allStepsComplete;
        const status = isCompleted ? 'COMPLETED' : 'IN_PROGRESS';

        console.log('[Save] Updating execution:', targetExecutionId, 'with status:', status);
        // Include disabled criteria keys in the saved criteria object
        const criteriaWithDisabledKeys = criteria ? {
          ...criteria,
          disabledCriteriaKeys: Array.from(disabledCriteria),
        } : {};
        const updateResult = await api.executions.update(targetCaseId, targetExecutionId, {
          questionnaire: questions,
          answers,
          indication: indication || {},
          criteria: criteriaWithDisabledKeys,
          completedSteps,
          currentStep,
          status,
        });
        console.log('[Save] Update result:', updateResult);
        console.log('[Save] Saved disabled criteria keys:', Array.from(disabledCriteria));

        // When execution is completed, also archive the case
        if (isCompleted) {
          console.log('[Save] Marking case as ARCHIVED:', targetCaseId);
          await api.cases.update(targetCaseId, { status: 'ARCHIVED' });
        }
      } else {
        console.error('[Save] Missing targetCaseId or targetExecutionId!', { targetCaseId, targetExecutionId });
      }

      return targetCaseId!;
    },
    onSuccess: (resultCaseId) => {
      console.log('[Save] Success, saved case ID:', resultCaseId);
      setSavedCaseId(resultCaseId);
      setLastSavedTime(new Date());
      // Invalidate queries so case list and detail pages show updated data
      queryClient.invalidateQueries({ queryKey: ['cases'] });
      queryClient.invalidateQueries({ queryKey: ['case', resultCaseId] });
    },
    onError: (error) => {
      console.error('[Save] Error:', error);
    },
  });

  // Quick save without completing - for "Save Progress" button
  const handleSaveProgress = () => {
    saveCaseMutation.mutate({ markComplete: false });
  };

  // Final save that marks execution as complete
  const handleSaveAndComplete = () => {
    saveCaseMutation.mutate({ markComplete: true });
  };

  const handleNext = () => {
    switch (currentStep) {
      case 'input':
        // If we already have questions, just go to questionnaire view
        if (questions.length > 0) {
          setCurrentStep('questionnaire');
        } else {
          setCurrentStep('questionnaire');
          generateQuestionnaireMutation.mutate();
        }
        break;
      case 'questionnaire':
        // If we already have questions (resumed), go directly to answers
        if (questions.length > 0) {
          setCurrentStep('answers');
        } else {
          // No questions yet - generate them
          generateQuestionnaireMutation.mutate();
        }
        break;
      case 'answers':
        // Mark answers as completed before analyzing - this triggers auto-save
        if (!completedSteps.includes('answers')) {
          setCompletedSteps(prev => [...prev, 'answers']);
        }
        // If we already have indication data (resumed), just show it
        if (indication && Object.keys(indication).length > 0) {
          setCurrentStep('indication');
        } else {
          setCurrentStep('indication');
          indicationMutation.mutate();
        }
        break;
      case 'indication':
        // If we already have criteria data (resumed), just show it
        if (criteria && Object.keys(criteria).length > 0) {
          setCurrentStep('criteria');
        } else {
          setCurrentStep('criteria');
          criteriaMutation.mutate();
        }
        break;
      case 'criteria':
        // If we already have recommendations, just show them
        const hasRecommendations = recommendations.gemini.length > 0;
        if (hasRecommendations) {
          setCurrentStep('recommendations');
        } else {
          setCurrentStep('recommendations');
          startAllSearches();
        }
        break;
      case 'recommendations':
        setCurrentStep('results');
        break;
    }
  };

  const handleBack = () => {
    const stepIndex = STEPS.findIndex((s) => s.id === currentStep);
    if (stepIndex > 0) {
      setCurrentStep(STEPS[stepIndex - 1].id);
    }
  };

  const isLoading =
    existingCaseQuery.isLoading ||
    createExecutionMutation.isPending ||
    generateSampleCaseMutation.isPending ||
    generateQuestionnaireMutation.isPending ||
    autofillMutation.isPending ||
    indicationMutation.isPending ||
    criteriaMutation.isPending ||
    saveCaseMutation.isPending;

  const currentStepIndex = STEPS.findIndex((s) => s.id === currentStep);

  const canProceedFromInput =
    condition.trim().length >= 10 &&
    patientInfo.age !== undefined &&
    patientInfo.gender !== undefined &&
    patientInfo.postalCode !== undefined;

  // Check if all REQUIRED questions have been answered (optional questions can be empty)
  const allQuestionsAnswered =
    questions.length > 0 &&
    questions.every((q) => !q.required || answers[q.id]?.trim());

  // Show loading state when fetching existing case
  if (isExistingCase && existingCaseQuery.isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-24">
        <div className="relative">
          <div className="h-20 w-20 rounded-full bg-primary-100 animate-pulse" />
          <Loader2 className="absolute inset-0 m-auto h-10 w-10 text-primary-600 animate-spin" />
        </div>
        <h2 className="mt-6 text-xl font-semibold font-display text-navy">Loading Case...</h2>
        <p className="mt-2 text-gray-500">Retrieving case data for analysis</p>
      </div>
    );
  }

  // Handle navigation back - invalidate queries so case list and detail refresh
  const handleNavigateBack = () => {
    // Invalidate case queries so the list and detail pages show updated data
    queryClient.invalidateQueries({ queryKey: ['cases'] });
    if (caseId) {
      queryClient.invalidateQueries({ queryKey: ['case', caseId] });
    }
    navigate(isExistingCase ? `/cases/${caseId}` : '/cases');
  };

  // Show error state if case fetch failed
  if (isExistingCase && existingCaseQuery.isError) {
    return (
      <div className="flex flex-col items-center justify-center py-24">
        <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center">
          <AlertTriangle className="h-8 w-8 text-red-600" />
        </div>
        <h2 className="mt-4 text-xl font-semibold text-navy">Error Loading Case</h2>
        <p className="mt-2 text-gray-500">{existingCaseQuery.error?.message || 'Failed to load case'}</p>
        <button
          onClick={() => navigate('/cases')}
          className="btn-primary mt-6"
        >
          Back to Cases
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Sticky Header with navigation and stepper - top-12 accounts for Layout header height (h-12 = 48px) */}
      <div className="sticky top-12 z-30 -mx-4 px-4 lg:-mx-6 lg:px-6 pt-2 pb-3 bg-gray-50 border-b border-gray-100 mb-4 shadow-sm">
        {/* Navigation and action buttons */}
        <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <button
            onClick={handleNavigateBack}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">
              {isExistingCase && existingCaseTitle ? existingCaseTitle : 'Quick Analysis'}
            </h1>
            <p className="text-xs text-gray-500">Step {currentStepIndex + 1} of {STEPS.length}</p>
          </div>
        </div>

        {/* Navigation buttons in header */}
        <div className="flex gap-2">
          {/* Save Progress button - visible after first step */}
          {currentStep !== 'input' && currentStep !== 'results' && (
            <div className="flex items-center gap-2">
              <button
                onClick={handleSaveProgress}
                data-action="save"
                className="btn-secondary flex items-center gap-1.5"
                disabled={saveCaseMutation.isPending}
              >
                {saveCaseMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : savedCaseId ? (
                  <CheckCircle className="h-4 w-4 text-green-500" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                {saveCaseMutation.isPending ? 'Saving...' : savedCaseId ? 'Saved' : 'Save Progress'}
              </button>
              {lastSavedTime && !saveCaseMutation.isPending && (
                <span className="text-xs text-gray-500">
                  {formatDistanceToNow(lastSavedTime, { addSuffix: true })}
                </span>
              )}
            </div>
          )}

          {currentStep !== 'results' && (
            <>
              <button
                onClick={handleBack}
                data-action="back"
                className="btn-secondary"
                disabled={currentStepIndex === 0 || isLoading}
              >
                <ArrowLeft className="mr-1 h-4 w-4" />
                Back
              </button>
              {/* AI Fill button - only on input step */}
              {currentStep === 'input' && (
                <button
                  onClick={() => generateSampleCaseMutation.mutate()}
                  disabled={generateSampleCaseMutation.isPending}
                  className="btn-outline"
                >
                  {generateSampleCaseMutation.isPending ? (
                    <>
                      <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-1.5 h-4 w-4" />
                      AI Fill
                    </>
                  )}
                </button>
              )}
              <button
                onClick={handleNext}
                data-action="next"
                className="btn-primary"
                disabled={isLoading || (currentStep === 'input' && !canProceedFromInput) || (currentStep === 'answers' && !allQuestionsAnswered)}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                    {currentStep === 'input' || currentStep === 'questionnaire'
                      ? 'Preparing questionnaire...'
                      : currentStep === 'answers'
                        ? 'Evaluating patient needs...'
                        : currentStep === 'indication'
                          ? 'Analyzing patient case...'
                          : currentStep === 'criteria'
                            ? 'Building matching criteria...'
                            : currentStep === 'recommendations'
                              ? 'Matching care providers...'
                              : 'Processing...'}
                  </>
                ) : (
                  <>
                    {currentStep === 'input'
                      ? 'Generate Questions'
                      : currentStep === 'questionnaire'
                        ? 'Answer Questions'
                        : currentStep === 'answers'
                          ? 'Analyze Case'
                          : currentStep === 'indication'
                            ? 'Generate Matching Criteria'
                            : currentStep === 'criteria'
                              ? 'Match Care Providers'
                              : currentStep === 'recommendations'
                                ? 'Complete Analysis'
                                : 'Next'}
                    <ArrowRight className="ml-1 h-4 w-4" />
                  </>
                )}
              </button>
            </>
          )}
        </div>
        </div>

        {/* Step Progress Indicator - 3 Line Design with Colored Phases */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 mt-3">
          {(() => {
            // Phase color definitions
            const phaseColors: Record<number, { bg: string; border: string; text: string; textMuted: string; stepBg: string; stepText: string; line: string }> = {
              1: { bg: 'bg-sky-50', border: 'border-sky-200', text: 'text-sky-700', textMuted: 'text-sky-600', stepBg: 'bg-sky-200', stepText: 'text-sky-800', line: 'bg-sky-300' },
              2: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', textMuted: 'text-amber-600', stepBg: 'bg-amber-200', stepText: 'text-amber-800', line: 'bg-amber-300' },
              3: { bg: 'bg-violet-50', border: 'border-violet-200', text: 'text-violet-700', textMuted: 'text-violet-600', stepBg: 'bg-violet-200', stepText: 'text-violet-800', line: 'bg-violet-300' },
              4: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', textMuted: 'text-emerald-600', stepBg: 'bg-emerald-200', stepText: 'text-emerald-800', line: 'bg-emerald-300' },
            };

            // Actor descriptions for each step
            const actorDescriptions: Record<string, { actor: string; action: string }> = {
              'input': { actor: 'Patient', action: 'enters data' },
              'questionnaire': { actor: 'AI', action: 'builds questionnaire' },
              'answers': { actor: 'Patient', action: 'fills answers' },
              'indication': { actor: 'Doctor', action: 'reviews analysis' },
              'criteria': { actor: 'Doctor', action: 'reviews criteria' },
              'recommendations': { actor: 'AI', action: 'matches providers' },
              'results': { actor: 'Doctor', action: 'saves case' },
            };

            // Group steps by phase with flex weights based on step count
            const phaseGroups = PHASES.map(phase => {
              const stepsInPhase = STEPS.filter(s => s.phase === phase.id);
              const stepIndices = stepsInPhase.map(s => STEPS.findIndex(st => st.id === s.id));
              const isPhaseActive = stepsInPhase.some(s => s.id === currentStep);
              const isPhaseComplete = stepIndices.every(i => i < currentStepIndex);
              // Flex grow based on number of steps (3 steps = 3, 2 steps = 2, 1 step = 1.5 for better balance)
              const flexGrow = stepsInPhase.length === 1 ? 1.2 : stepsInPhase.length;
              return { ...phase, steps: stepsInPhase, stepIndices, isPhaseActive, isPhaseComplete, flexGrow };
            });

            return (
              <div className="flex rounded-lg overflow-hidden border border-gray-200">
                {phaseGroups.map((phase, phaseIdx) => {
                  const colors = phaseColors[phase.id];
                  const isLastPhase = phaseIdx === phaseGroups.length - 1;
                  const isFirstPhase = phaseIdx === 0;

                  return (
                    <div key={`phase-${phase.id}`} className="contents">
                      <div
                        className={clsx('overflow-hidden', !isLastPhase && 'border-r border-gray-200')}
                        style={{ flex: phase.flexGrow }}
                      >
                        {/* Line 1: Phase Title */}
                        <div className={clsx('px-2 py-2', colors.bg)}>
                          <span className={clsx('text-[11px] font-semibold whitespace-nowrap', colors.text)}>
                            Phase {phase.id}: {phase.name}
                          </span>
                        </div>

                        {/* Line 2: Steps */}
                        <div className={clsx('flex items-center py-3', colors.bg)}>
                          {phase.steps.map((step, stepIdx) => {
                            const globalIndex = STEPS.findIndex(s => s.id === step.id);
                            const isActive = step.id === currentStep;
                            const isComplete = globalIndex < currentStepIndex;
                            const isLastStepInPhase = stepIdx === phase.steps.length - 1;
                            const isFirstStepInPhase = stepIdx === 0;

                            const hasStepData = (() => {
                              switch (step.id) {
                                case 'input': return true;
                                case 'questionnaire': return questions.length > 0;
                                case 'answers': return questions.length > 0;
                                case 'indication': return indication && Object.keys(indication).length > 0;
                                case 'criteria': return criteria && Object.keys(criteria).length > 0;
                                case 'recommendations': return recommendations.gemini.length > 0;
                                case 'results': return false;
                                default: return false;
                              }
                            })();
                            const canNavigate = isComplete || isActive || hasStepData;

                            const handleStepClick = () => {
                              if (!canNavigate || isActive) return;
                              setCurrentStep(step.id);
                            };

                            // Determine connector colors
                            const prevStepComplete = globalIndex - 1 < currentStepIndex;
                            const thisStepComplete = globalIndex < currentStepIndex;

                            return (
                              <div key={step.id} className="flex items-center flex-1">
                                {/* Leading connector */}
                                {(isFirstStepInPhase && !isFirstPhase) || stepIdx > 0 ? (
                                  <div className={clsx(
                                    'h-0.5 flex-1',
                                    prevStepComplete ? 'bg-primary-500' : 'bg-gray-300'
                                  )} />
                                ) : isFirstPhase && isFirstStepInPhase ? (
                                  <div className="flex-1" /> // Spacer for first step alignment
                                ) : null}

                                {/* Step Circle */}
                                <button
                                  onClick={handleStepClick}
                                  disabled={!canNavigate}
                                  className={clsx(
                                    'flex-shrink-0 transition-all',
                                    canNavigate && !isActive ? 'cursor-pointer' : '',
                                    !canNavigate ? 'cursor-not-allowed' : ''
                                  )}
                                  title={step.label}
                                  aria-label={`Step ${globalIndex + 1}: ${step.label}`}
                                  aria-current={isActive ? 'step' : undefined}
                                >
                                  <div
                                    className={clsx(
                                      'flex h-10 w-10 items-center justify-center rounded-full text-sm font-bold transition-all duration-200',
                                      isComplete
                                        ? 'bg-primary-600 text-white shadow-sm'
                                        : isActive
                                          ? 'bg-primary-700 text-white shadow-lg ring-4 ring-primary-200'
                                          : hasStepData
                                            ? clsx(colors.stepBg, colors.stepText, 'hover:opacity-80')
                                            : 'bg-white text-gray-400 border-2 border-gray-300'
                                    )}
                                  >
                                    {isComplete ? (
                                      <Check className="h-5 w-5" aria-hidden="true" />
                                    ) : (
                                      <span>{globalIndex + 1}</span>
                                    )}
                                  </div>
                                </button>

                                {/* Trailing connector */}
                                {(isLastStepInPhase && !isLastPhase) || !isLastStepInPhase ? (
                                  <div className={clsx(
                                    'h-0.5 flex-1',
                                    thisStepComplete ? 'bg-primary-500' : 'bg-gray-300'
                                  )} />
                                ) : isLastPhase && isLastStepInPhase ? (
                                  <div className="flex-1" /> // Spacer for last step alignment
                                ) : null}
                              </div>
                            );
                          })}
                        </div>

                        {/* Line 3: Actor labels */}
                        <div className={clsx('flex items-start py-2', colors.bg)}>
                          {phase.steps.map((step) => {
                            const globalIndex = STEPS.findIndex(s => s.id === step.id);
                            const isActive = step.id === currentStep;
                            const isComplete = globalIndex < currentStepIndex;
                            const desc = actorDescriptions[step.id];

                            return (
                              <div key={`label-${step.id}`} className="flex-1 flex flex-col items-center text-center px-1">
                                <span className={clsx(
                                  'text-[11px] font-semibold',
                                  isActive ? colors.text : isComplete ? 'text-primary-600' : colors.textMuted
                                )}>
                                  {desc.actor}
                                </span>
                                <span className={clsx(
                                  'text-[10px] leading-tight',
                                  isActive ? colors.textMuted : isComplete ? 'text-primary-500' : 'text-gray-400'
                                )}>
                                  {desc.action}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                    </div>
                  );
                })}
              </div>
            );
          })()}

          {/* Current step title - visible on mobile */}
          <div className="sm:hidden mt-3 pt-2 border-t border-gray-100 text-center">
            <span className="text-xs font-medium text-primary-700">
              Step {currentStepIndex + 1}: {STEPS[currentStepIndex].label}
            </span>
          </div>
        </div>
      </div>

      {/* Case Summary Panel - shown for existing cases */}
      {isExistingCase && (condition || patientInfo.age || patientInfo.gender || patientInfo.canton) && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
          <div className="flex items-start gap-4">
            {/* Patient Demographics */}
            <div className="flex items-center gap-3 text-sm shrink-0">
              <div className="flex items-center gap-1.5 text-gray-600">
                <User className="h-4 w-4 text-gray-400" />
                <span className="font-medium">Patient:</span>
              </div>
              <div className="flex items-center gap-2 text-gray-700">
                {patientInfo.age && <span>{patientInfo.age} yrs</span>}
                {patientInfo.gender && (
                  <span className="capitalize">{patientInfo.gender}</span>
                )}
                {(patientInfo.canton || patientInfo.postalCode) && (
                  <span className="flex items-center gap-1">
                    <MapPin className="h-3.5 w-3.5 text-gray-400" />
                    {patientInfo.postalCode && <span>{patientInfo.postalCode}</span>}
                    {patientInfo.canton && (
                      <span className="text-gray-500">
                        ({SWISS_CANTONS.find(c => c.code === patientInfo.canton)?.name || patientInfo.canton})
                      </span>
                    )}
                  </span>
                )}
              </div>
            </div>

            {/* Chief Complaint Preview */}
            {condition && (
              <div className="flex-1 min-w-0 border-l border-gray-200 pl-4">
                <div className="text-xs text-gray-500 mb-0.5">Chief Complaint</div>
                <p className="text-sm text-gray-700 line-clamp-2">{condition}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Step Content */}
      <div className="card">
        {/* Step 1: Patient Info Input */}
        {currentStep === 'input' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Main: Chief Complaint */}
            <div className="lg:col-span-2">
              <label className="form-label form-label-required">Chief Complaint</label>
              <textarea
                value={condition}
                onChange={(e) => setCondition(e.target.value)}
                rows={12}
                className="input resize-none"
                placeholder="Describe the patient's chief complaint in detail. Include the main reason for seeking care, symptoms, duration, relevant medical history, and any specific requirements for the care provider..."
              />
              <p className="mt-1.5 text-xs text-gray-500">
                {condition.length < 10 ? (
                  <span className="text-red-500">Please provide at least 10 characters</span>
                ) : (
                  <span className="text-primary-600">{condition.length} characters</span>
                )}
              </p>
            </div>

            {/* Sidebar: Patient Details */}
            <div className="space-y-3">
              <div className="text-sm font-medium text-gray-700 mb-2">Patient Details <span className="text-red-500">*</span></div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="label text-xs">
                    Age <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={patientInfo.age || ''}
                    onChange={(e) => setPatientInfo({ ...patientInfo, age: e.target.value ? parseInt(e.target.value) : undefined })}
                    className={clsx('input mt-0.5', !patientInfo.age && 'border-gray-300')}
                    placeholder="45"
                    min={0}
                    max={120}
                  />
                </div>
                <div>
                  <label className="label text-xs">
                    Gender <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={patientInfo.gender || ''}
                    onChange={(e) => setPatientInfo({ ...patientInfo, gender: (e.target.value || undefined) as PatientInfo['gender'] })}
                    className={clsx('input mt-0.5', !patientInfo.gender && 'border-gray-300')}
                  >
                    <option value="">Select</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
              <div>
                <PostalCodeAutocomplete
                  value={patientInfo.postalCode || ''}
                  onChange={(plz, canton, cityName) => setPatientInfo({ ...patientInfo, postalCode: plz, canton, cityName })}
                  label="Postal Code"
                  required
                />
              </div>
              <div>
                <label className="label text-xs">Canton</label>
                <input
                  type="text"
                  value={patientInfo.canton ? SWISS_CANTONS.find(c => c.code === patientInfo.canton)?.name || patientInfo.canton : ''}
                  readOnly
                  className="input mt-0.5 bg-gray-50 text-gray-500"
                  placeholder="Auto-filled from postal code"
                />
              </div>

              {/* What happens next info box */}
              <div className="bg-primary-50 border border-primary-100 rounded-lg p-3 mt-4">
                <p className="text-xs text-primary-800 font-medium mb-1">What happens next?</p>
                <p className="text-xs text-primary-700">
                  Claude AI will analyze the chief complaint and generate 15-20 tailored medical questions.
                </p>
                <p className="text-xs text-primary-600 mt-1 flex items-center gap-1">
                  <Loader2 className="h-3 w-3" aria-hidden="true" />
                  Typically takes 30-60 seconds
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Generate Questions (automatic) */}
        {currentStep === 'questionnaire' && (
          <div className="flex flex-col items-center justify-center py-12">
            {generateQuestionnaireMutation.isPending ? (
              <>
                <div className="relative">
                  <div className="h-20 w-20 rounded-full bg-primary-100 animate-pulse" />
                  <Loader2 className="absolute inset-0 m-auto h-10 w-10 text-primary-600 animate-spin" />
                </div>
                <h2 className="mt-6 text-xl font-semibold font-display text-navy">Generating Patient Questionnaire...</h2>
                <p className="mt-2 text-gray-500 text-center max-w-md">
                  Reviewing the chief complaint to create relevant questions that will help identify the best care provider for this case
                </p>
                <div className="mt-6 w-64">
                  <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-primary-500 rounded-full animate-[progress_120s_ease-in-out_forwards]" />
                  </div>
                  <p className="mt-2 text-xs text-gray-400 text-center">This may take a few minutes</p>
                </div>
              </>
            ) : generateQuestionnaireMutation.isError ? (
              <>
                <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertTriangle className="h-8 w-8 text-red-600" />
                </div>
                <h2 className="mt-4 text-xl font-semibold text-navy">Error Generating Questions</h2>
                <p className="mt-2 text-gray-500">{generateQuestionnaireMutation.error?.message}</p>
                <button
                  onClick={() => generateQuestionnaireMutation.mutate()}
                  className="btn-primary mt-4"
                >
                  Try Again
                </button>
              </>
            ) : questions.length > 0 ? (
              <div className="w-full max-w-2xl">
                <div className="flex flex-col items-center mb-6">
                  <div className="h-14 w-14 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle className="h-7 w-7 text-green-600" />
                  </div>
                  <h2 className="mt-3 text-lg font-semibold text-navy">Patient Questionnaire Ready</h2>
                  <p className="mt-1 text-sm text-gray-500">
                    {questions.length} questions generated. Click "Answer Questions" to proceed.
                  </p>
                </div>
                {/* Question Preview */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">Preview of questions:</p>
                  <div className="space-y-2">
                    {questions.slice(0, 4).map((q, i) => (
                      <div key={q.id} className="flex items-start gap-2 p-2 bg-white rounded border border-gray-100">
                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold flex items-center justify-center">
                          {i + 1}
                        </span>
                        <span className="text-sm text-gray-700 line-clamp-1">{q.question}</span>
                      </div>
                    ))}
                    {questions.length > 4 && (
                      <p className="text-xs text-gray-400 text-center pt-2">
                        + {questions.length - 4} more questions
                      </p>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-3 text-center">
                    Estimated time: 5-10 minutes
                  </p>
                </div>
              </div>
            ) : (
              <>
                <ListChecks className="h-16 w-16 text-primary-500" />
                <h2 className="mt-4 text-xl font-semibold text-navy">Generate Patient Questionnaire</h2>
                <p className="mt-2 text-gray-500 max-w-md text-center">
                  Claude will analyze the patient's chief complaint and generate a tailored patient questionnaire.
                </p>
              </>
            )}
          </div>
        )}

        {/* Step 3: Answer Questions */}
        {currentStep === 'answers' && questions.length > 0 && (
          <div className="flex flex-col">
            {/* Progress bar */}
            <div className="mb-4 p-3 bg-white border border-gray-200 rounded-lg">
              <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span className="font-medium">Progress</span>
                <span className="font-semibold text-primary-700">
                  {Math.round((Object.keys(answers).length / questions.length) * 100)}% complete
                </span>
              </div>
              <div className="h-2.5 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary-500 rounded-full transition-all duration-300"
                  style={{ width: `${(Object.keys(answers).length / questions.length) * 100}%` }}
                />
              </div>
              <div className="flex justify-between text-xs text-gray-500 mt-1.5">
                <span>{Object.keys(answers).length} of {questions.length} questions answered</span>
                {Object.keys(answers).length < questions.length && (
                  <span>{questions.length - Object.keys(answers).length} remaining</span>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <ListChecks className="h-4 w-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-900">
                    Medical Questions
                  </span>
                </div>
                {specialistRole && (
                  <span className="text-xs text-primary-600 bg-primary-50 px-2 py-0.5 rounded">
                    {specialistRole}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => autofillMutation.mutate()}
                  disabled={autofillMutation.isPending}
                  className="btn-outline text-xs px-2.5 py-1"
                >
                  {autofillMutation.isPending ? (
                    <>
                      <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                      Auto-filling...
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-1.5 h-3.5 w-3.5" />
                      AI Auto-fill
                    </>
                  )}
                </button>
                <button
                  onClick={() => setShowSendToPatientModal(true)}
                  className="btn-outline text-xs px-2.5 py-1"
                >
                  <Mail className="mr-1.5 h-3.5 w-3.5" />
                  Send to Patient
                </button>
              </div>
            </div>

            <div className="space-y-3">
              {questions.map((question, index) => (
                <div key={question.id} className="pb-3 border-b border-gray-50 last:border-0 last:pb-0">
                  <div className="flex items-start gap-2">
                    <span className="text-xs font-semibold text-primary-600 bg-primary-50 rounded w-6 h-6 flex items-center justify-center shrink-0 mt-0.5">
                      {index + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        {question.question}
                        {question.required && <span className="text-red-500 ml-0.5">*</span>}
                      </p>

                      {/* Select dropdown */}
                      {question.type === 'select' && question.options ? (
                        <select
                          value={answers[question.id] || ''}
                          onChange={(e) => setAnswers({ ...answers, [question.id]: e.target.value })}
                          className="input mt-2 py-1.5"
                        >
                          <option value="">Select...</option>
                          {question.options.map((opt) => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      ) : /* Multiselect badges */
                      question.type === 'multiselect' && question.options ? (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {question.options.map((opt) => {
                            const answerValue = answers[question.id] || '';
                            const currentAnswers = answerValue.split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
                            const selected = currentAnswers.some(a =>
                              a === opt.toLowerCase() ||
                              opt.toLowerCase().includes(a) ||
                              a.includes(opt.toLowerCase())
                            );
                            return (
                              <button
                                key={opt}
                                type="button"
                                onClick={() => {
                                  const rawAnswers = answerValue.split(',').map(s => s.trim()).filter(Boolean);
                                  const isCurrentlySelected = rawAnswers.some(a =>
                                    a.toLowerCase() === opt.toLowerCase()
                                  );
                                  const newValue = isCurrentlySelected
                                    ? rawAnswers.filter(v => v.toLowerCase() !== opt.toLowerCase())
                                    : [...rawAnswers, opt];
                                  setAnswers({ ...answers, [question.id]: newValue.join(', ') });
                                }}
                                className={clsx(
                                  'px-2 py-1 text-xs rounded border transition-all duration-150',
                                  selected
                                    ? 'bg-primary-100 border-primary-400 text-primary-700 font-medium'
                                    : 'bg-white border-gray-200 text-gray-600 hover:border-primary-300 hover:bg-primary-50'
                                )}
                              >
                                {opt}
                              </button>
                            );
                          })}
                        </div>
                      ) : /* Scale slider */
                      question.type === 'scale' ? (
                        <div className="flex items-center gap-3 mt-2">
                          <span className="text-xs text-gray-400">0</span>
                          <input
                            type="range"
                            min="0"
                            max="10"
                            value={answers[question.id] || '5'}
                            onChange={(e) => setAnswers({ ...answers, [question.id]: e.target.value })}
                            className="flex-1 h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-600"
                          />
                          <span className="text-xs text-gray-400">10</span>
                          <span className="min-w-[28px] text-center text-sm font-bold text-primary-700 bg-primary-50 rounded px-1.5 py-0.5">
                            {answers[question.id] || '5'}
                          </span>
                        </div>
                      ) : /* Single choice radio buttons */
                      question.type === 'single_choice' && question.options ? (
                        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1.5">
                          {question.options.map((option) => (
                            <label key={option} className="flex items-center gap-2 cursor-pointer group">
                              <input
                                type="radio"
                                name={question.id}
                                value={option}
                                checked={answers[question.id] === option}
                                onChange={(e) =>
                                  setAnswers({ ...answers, [question.id]: e.target.value })
                                }
                                className="h-3.5 w-3.5 text-primary-600 focus:ring-primary-500"
                              />
                              <span className="text-sm text-gray-700 group-hover:text-navy">{option}</span>
                            </label>
                          ))}
                        </div>
                      ) : /* Boolean yes/no */
                      question.type === 'boolean' ? (
                        <div className="mt-2 flex gap-4">
                          {['Yes', 'No'].map((val) => (
                            <label key={val} className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name={question.id}
                                value={val}
                                checked={answers[question.id] === val}
                                onChange={(e) =>
                                  setAnswers({ ...answers, [question.id]: e.target.value })
                                }
                                className="h-3.5 w-3.5 text-primary-600 focus:ring-primary-500"
                              />
                              <span className="text-sm text-gray-700">{val}</span>
                            </label>
                          ))}
                        </div>
                      ) : /* Default: text textarea */
                      (
                        <textarea
                          value={answers[question.id] || ''}
                          onChange={(e) => setAnswers({ ...answers, [question.id]: e.target.value })}
                          rows={2}
                          className="input mt-2 py-1.5 text-sm"
                          placeholder="Enter your answer..."
                        />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Document Upload Section - only for standalone mode */}
            {!isExistingCase && (
              <div className="mt-6 pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-gray-400" />
                    <span className="text-sm font-medium text-gray-700">Supporting Documents</span>
                    <span className="text-xs text-gray-400">(Optional)</span>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mb-3">
                  Upload any relevant medical records, lab results, or imaging reports that may help with the analysis.
                </p>

                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="border border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-primary-400 hover:bg-gray-50 transition-colors"
                >
                  <Upload className="mx-auto h-6 w-6 text-gray-400" />
                  <p className="mt-1 text-xs text-gray-600">Click to upload documents</p>
                  <p className="text-xs text-gray-400">PDF, images, or Word documents</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    onChange={(e) => {
                      const selectedFiles = e.target.files;
                      if (!selectedFiles) return;
                      const newFiles = Array.from(selectedFiles).map((file) => ({
                        file,
                        id: crypto.randomUUID(),
                      }));
                      setUploadedFiles((prev) => [...prev, ...newFiles]);
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                    }}
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
                    className="hidden"
                  />
                </div>

                {uploadedFiles.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {uploadedFiles.map((uploadedFile) => (
                      <div
                        key={uploadedFile.id}
                        className="flex items-center gap-2 rounded-lg bg-gray-50 px-2 py-1.5 group"
                      >
                        <span className="text-base flex-shrink-0">
                          {uploadedFile.file.type.startsWith('image/') ? '🖼️' : uploadedFile.file.type === 'application/pdf' ? '📄' : '📎'}
                        </span>
                        <span className="text-xs text-gray-700 truncate flex-1">
                          {uploadedFile.file.name}
                        </span>
                        <span className="text-xs text-gray-400">
                          {uploadedFile.file.size < 1024
                            ? `${uploadedFile.file.size} B`
                            : uploadedFile.file.size < 1024 * 1024
                              ? `${(uploadedFile.file.size / 1024).toFixed(1)} KB`
                              : `${(uploadedFile.file.size / (1024 * 1024)).toFixed(1)} MB`}
                        </span>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          {(uploadedFile.file.type.startsWith('image/') || uploadedFile.file.type === 'application/pdf') && (
                            <button
                              type="button"
                              onClick={() => setPreviewLocalFile(uploadedFile)}
                              className="p-1 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded"
                              title="Preview"
                            >
                              <Eye className="h-3.5 w-3.5" />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => setUploadedFiles((prev) => prev.filter((f) => f.id !== uploadedFile.id))}
                            className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
                            title="Remove"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Existing Case Documents Section - only for existing case mode */}
            {isExistingCase && existingCaseQuery.data?.data?.documents && existingCaseQuery.data.data.documents.length > 0 && (
              <div className="mt-6 pt-4 border-t border-gray-200">
                <div className="flex items-center gap-2 mb-3">
                  <FileText className="h-4 w-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-700">Documents from Case</span>
                  <span className="text-xs text-gray-400">({existingCaseQuery.data.data.documents.length})</span>
                </div>

                <div className="space-y-1">
                  {existingCaseQuery.data.data.documents.map((doc: CaseDocument) => (
                    <div
                      key={doc.id}
                      className="flex items-center gap-2 rounded-lg bg-gray-50 px-2 py-1.5 group"
                    >
                      <span className="text-base flex-shrink-0">
                        {doc.mimeType.startsWith('image/') ? '🖼️' : doc.mimeType === 'application/pdf' ? '📄' : '📎'}
                      </span>
                      <span className="text-xs text-gray-700 truncate flex-1">
                        {doc.originalName}
                      </span>
                      <span className="text-xs text-gray-400">
                        {(doc.size / 1024).toFixed(1)} KB
                      </span>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          type="button"
                          onClick={() => setPreviewDocument(doc)}
                          className="p-1 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded"
                          title="Preview"
                        >
                          <Eye className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => downloadDocument(doc.url, doc.originalName)}
                          className="p-1 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded"
                          title="Download"
                        >
                          <Download className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 4: Indication Analysis */}
        {currentStep === 'indication' && (
          <div className={indicationMutation.isPending ? "flex flex-col items-center justify-center py-12" : ""}>
            {indicationMutation.isPending ? (
              <>
                <div className="relative">
                  <div className="h-16 w-16 rounded-full bg-primary-100 animate-pulse" />
                  <Brain className="absolute inset-0 m-auto h-8 w-8 text-primary-600 animate-bounce" />
                </div>
                <h2 className="mt-4 text-lg font-semibold text-gray-900">Analyzing Patient Responses...</h2>
                <p className="mt-1 text-sm text-gray-500">
                  Evaluating clinical appropriateness and understanding patient needs to determine the right type of care provider
                </p>
              </>
            ) : indication ? (
              <div className="w-full">
                {/* Header with timestamp */}
                <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-100">
                  <h2 className="text-sm font-medium text-gray-900">Case Analysis Results</h2>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    {analysisTimestamp && (
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Analyzed {Math.round((Date.now() - analysisTimestamp.getTime()) / 60000)} min ago
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <CheckCircle className="h-3.5 w-3.5 text-green-500" />
                      Complete
                    </span>
                  </div>
                </div>

                {/* Document Verification Warning - Expandable */}
                {(indication as { documentFindings?: { hasInconsistencies?: boolean } }).documentFindings?.hasInconsistencies && (
                  <div className="mb-4 rounded-lg border-2 border-amber-300 bg-gradient-to-r from-amber-50 to-amber-100/50 overflow-hidden">
                    <button
                      onClick={() => setWarningExpanded(!warningExpanded)}
                      className="w-full p-3 flex items-center justify-between text-left hover:bg-amber-100/50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-1.5 rounded-full bg-amber-100 shrink-0">
                          <AlertTriangle className="h-4 w-4 text-amber-600" />
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold text-amber-800">
                            Document Verification Warning
                          </h3>
                          <p className="text-xs text-amber-700 mt-0.5">
                            Inconsistencies detected between documents and self-reported info
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-amber-700 font-medium">
                          {warningExpanded ? 'Hide' : 'Show'} Details
                        </span>
                        {warningExpanded ? (
                          <ChevronUp className="h-4 w-4 text-amber-600" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-amber-600" />
                        )}
                      </div>
                    </button>
                    {warningExpanded && (
                      <div className="px-4 pb-3 border-t border-amber-200">
                        <div className="pt-3 space-y-2">
                          {((indication as { documentFindings?: { inconsistencies?: string[] } }).documentFindings?.inconsistencies || []).map((item, i) => (
                            <div key={i} className="flex items-start gap-2 text-xs text-amber-800 bg-white px-3 py-2 rounded border border-amber-200">
                              <AlertCircle className="h-3.5 w-3.5 text-amber-600 mt-0.5 shrink-0" />
                              <span>{item}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Tab Navigation */}
                <div className="flex gap-1 mb-4 p-1 bg-gray-100 rounded-lg">
                  {[
                    { id: 'summary' as const, label: 'Summary', icon: CheckCircle },
                    { id: 'assessment' as const, label: 'Medical Assessment', icon: Brain },
                    { id: 'expectations' as const, label: 'Patient Expectations', icon: Users },
                    ...(((indication as { documentFindings?: { documentsReviewed?: boolean } }).documentFindings?.documentsReviewed)
                      ? [{ id: 'documents' as const, label: 'Documents', icon: FileText }]
                      : []),
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveAnalysisTab(tab.id)}
                      className={clsx(
                        'flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-all',
                        activeAnalysisTab === tab.id
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      )}
                    >
                      <tab.icon className="h-3.5 w-3.5" />
                      <span className="hidden sm:inline">{tab.label}</span>
                    </button>
                  ))}
                </div>

                {/* Tab Content */}
                <div className="space-y-4">
                  {/* Summary Tab */}
                  {activeAnalysisTab === 'summary' && (
                    <div className="p-4 rounded-lg border border-gray-200 bg-white">
                      {/* Suitability Verdict */}
                      <div className="flex items-start justify-between mb-4 pb-4 border-b border-gray-100">
                        <div className="flex items-center gap-3">
                          <div className={clsx(
                            'p-2 rounded-lg',
                            (indication as { indicationAnalysis?: { isAppropriate?: boolean } }).indicationAnalysis?.isAppropriate !== false
                              ? 'bg-green-100'
                              : 'bg-red-100'
                          )}>
                            {(indication as { indicationAnalysis?: { isAppropriate?: boolean } }).indicationAnalysis?.isAppropriate !== false ? (
                              <CheckCircle className="h-5 w-5 text-green-600" />
                            ) : (
                              <AlertCircle className="h-5 w-5 text-red-600" />
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold text-gray-900">Suitability:</span>
                              <span className={clsx(
                                'px-2 py-0.5 text-xs font-bold rounded uppercase',
                                (indication as { indicationAnalysis?: { isAppropriate?: boolean } }).indicationAnalysis?.isAppropriate !== false
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-red-100 text-red-700'
                              )}>
                                {(indication as { indicationAnalysis?: { isAppropriate?: boolean } }).indicationAnalysis?.isAppropriate !== false ? 'SUITABLE' : 'NOT SUITABLE'}
                              </span>
                            </div>
                            <p className="text-xs text-gray-600 mt-1">
                              {/* Use dynamic suitabilityReasoning from Claude if available, otherwise fallback */}
                              {(indication as { indicationAnalysis?: { suitabilityReasoning?: string } }).indicationAnalysis?.suitabilityReasoning ||
                                ((indication as { indicationAnalysis?: { isAppropriate?: boolean } }).indicationAnalysis?.isAppropriate !== false
                                  ? 'This case is appropriate for care provider referral based on the medical indication.'
                                  : 'This case may not be appropriate for care provider referral. Review the assessment for details.')}
                            </p>
                          </div>
                        </div>
                        {/* Confidence Score - use dynamic confidencePercent if available */}
                        {((indication as { indicationAnalysis?: { confidencePercent?: number; confidenceLevel?: string } }).indicationAnalysis?.confidencePercent ||
                          (indication as { indicationAnalysis?: { confidence?: string } }).indicationAnalysis?.confidence) && (
                          <div className="text-right">
                            <div className="text-xs text-gray-500 mb-1">Confidence</div>
                            {(() => {
                              const analysis = (indication as { indicationAnalysis?: { confidencePercent?: number; confidenceLevel?: string; confidence?: string } }).indicationAnalysis;
                              // Use confidencePercent if available (new dynamic format)
                              const percent = analysis?.confidencePercent;
                              // Determine level from confidenceLevel (new) or confidence (legacy)
                              const level = analysis?.confidenceLevel || analysis?.confidence;
                              // Calculate display percent: use actual percent or fallback based on level
                              const displayPercent = percent ?? (level === 'high' ? 94 : level === 'medium' ? 75 : 50);
                              return (
                                <div className={clsx(
                                  'text-lg font-bold',
                                  displayPercent >= 75 && 'text-green-600',
                                  displayPercent >= 50 && displayPercent < 75 && 'text-yellow-600',
                                  displayPercent < 50 && 'text-red-600'
                                )}>
                                  {displayPercent}%
                                </div>
                              );
                            })()}
                          </div>
                        )}
                      </div>

                      {/* Key Points with numbered badges */}
                      <div>
                        <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Key Points</h4>
                        <div className="space-y-2">
                          {(() => {
                            let pointNumber = 0;
                            const points: React.ReactNode[] = [];

                            // Add indication as first point
                            if ((indication as { indicationAnalysis?: { statedIndication?: string } }).indicationAnalysis?.statedIndication) {
                              pointNumber++;
                              points.push(
                                <div key="indication" className="flex items-start gap-2 text-xs">
                                  <span className="w-5 h-5 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-[10px] font-bold shrink-0">
                                    {pointNumber}
                                  </span>
                                  <span className="text-gray-700">
                                    <strong>Indication:</strong> {(indication as { indicationAnalysis: { statedIndication: string } }).indicationAnalysis.statedIndication}
                                  </span>
                                </div>
                              );
                            }

                            // Add critical/warning issues
                            ((indication as { potentialIssues?: Array<{ severity: string; issue: string }> }).potentialIssues || [])
                              .filter(i => i.severity === 'critical' || i.severity === 'warning')
                              .slice(0, 2)
                              .forEach((issue, i) => {
                                pointNumber++;
                                points.push(
                                  <div key={`issue-${i}`} className="flex items-start gap-2 text-xs">
                                    <span className={clsx(
                                      'w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0',
                                      issue.severity === 'critical' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                                    )}>
                                      {pointNumber}
                                    </span>
                                    <span className="text-gray-700">{issue.issue}</span>
                                  </div>
                                );
                              });

                            // Add criteria adjustments
                            if (((indication as { adjustedCriteria?: { addNew?: string[] } }).adjustedCriteria?.addNew?.length ?? 0) > 0) {
                              pointNumber++;
                              points.push(
                                <div key="criteria" className="flex items-start gap-2 text-xs">
                                  <span className="w-5 h-5 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-[10px] font-bold shrink-0">
                                    {pointNumber}
                                  </span>
                                  <span className="text-gray-700">
                                    <strong>{(indication as { adjustedCriteria: { addNew: string[] } }).adjustedCriteria.addNew.length} suggested criteria adjustments</strong> based on case analysis
                                  </span>
                                </div>
                              );
                            }

                            return points;
                          })()}
                        </div>
                      </div>

                      {/* Matching Considerations (collapsible) */}
                      {((indication as { potentialIssues?: Array<{ severity: string; issue: string; recommendation?: string }> }).potentialIssues?.length ?? 0) > 0 && (
                        <details className="mt-4 pt-4 border-t border-gray-100 group" open>
                          <summary className="flex items-center justify-between cursor-pointer list-none">
                            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                              Matching Considerations ({(indication as { potentialIssues: Array<{ severity: string }> }).potentialIssues.length})
                            </h4>
                            <ChevronDown className="h-4 w-4 text-gray-400 transition-transform group-open:rotate-180" />
                          </summary>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
                            {(indication as { potentialIssues: Array<{ severity: string; issue: string; recommendation?: string }> }).potentialIssues.map(
                              (item, i) => (
                                <div
                                  key={i}
                                  className={clsx(
                                    'p-2.5 rounded-md text-xs',
                                    item.severity === 'critical' && 'bg-red-50 border-l-3 border-l-red-500',
                                    item.severity === 'warning' && 'bg-yellow-50 border-l-3 border-l-yellow-500',
                                    item.severity === 'info' && 'bg-blue-50 border-l-3 border-l-blue-500'
                                  )}
                                >
                                  <div className="flex items-start gap-2">
                                    <span
                                      className={clsx(
                                        'px-1.5 py-0.5 text-[9px] font-bold uppercase rounded shrink-0',
                                        item.severity === 'critical' && 'bg-red-100 text-red-700',
                                        item.severity === 'warning' && 'bg-yellow-100 text-yellow-700',
                                        item.severity === 'info' && 'bg-blue-100 text-blue-700'
                                      )}
                                    >
                                      {item.severity}
                                    </span>
                                    <div className="flex-1 min-w-0">
                                      <div className="font-medium text-gray-800">{item.issue}</div>
                                      {item.recommendation && (
                                        <div className="text-gray-600 mt-0.5">{item.recommendation}</div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )
                            )}
                          </div>
                        </details>
                      )}
                    </div>
                  )}

                  {/* Medical Assessment Tab */}
                  {activeAnalysisTab === 'assessment' && (
                    <div className="p-4 rounded-lg border border-gray-200 bg-white">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className="p-1.5 rounded-md bg-blue-100">
                            <Brain className="h-4 w-4 text-blue-600" />
                          </div>
                          <span className="text-sm font-medium text-gray-900">Medical Assessment</span>
                        </div>
                        {(indication as { indicationAnalysis?: { confidence?: string } }).indicationAnalysis?.confidence && (
                          <span
                            className={clsx(
                              'px-2 py-0.5 text-xs font-medium rounded',
                              (indication as { indicationAnalysis: { confidence: string } }).indicationAnalysis.confidence === 'high' && 'bg-green-100 text-green-700',
                              (indication as { indicationAnalysis: { confidence: string } }).indicationAnalysis.confidence === 'medium' && 'bg-yellow-100 text-yellow-700',
                              (indication as { indicationAnalysis: { confidence: string } }).indicationAnalysis.confidence === 'low' && 'bg-red-100 text-red-700'
                            )}
                          >
                            {(indication as { indicationAnalysis: { confidence: string } }).indicationAnalysis.confidence} confidence
                          </span>
                        )}
                      </div>

                      {(indication as { indicationAnalysis?: { statedIndication?: string } }).indicationAnalysis?.statedIndication && (
                        <div className="mb-3">
                          <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">Stated Indication</span>
                          <p className="text-sm font-medium text-gray-800 mt-1">
                            {(indication as { indicationAnalysis: { statedIndication: string } }).indicationAnalysis.statedIndication}
                          </p>
                        </div>
                      )}

                      {/* ICD-10 Codes */}
                      {((indication as { icd10Codes?: { leadingCondition?: string | null; otherConditions?: string[] } }).icd10Codes?.leadingCondition ||
                        ((indication as { icd10Codes?: { otherConditions?: string[] } }).icd10Codes?.otherConditions?.length ?? 0) > 0) && (
                        <div className="mb-3 p-2.5 bg-blue-50 rounded-lg border border-blue-100">
                          <span className="text-[10px] font-medium text-blue-600 uppercase tracking-wide flex items-center gap-1">
                            <FileText className="h-3 w-3" />
                            ICD-10 Codes
                          </span>
                          <div className="mt-2 flex flex-wrap gap-2">
                            {(indication as { icd10Codes?: { leadingCondition?: string | { code: string; name?: string } | null } }).icd10Codes?.leadingCondition && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-mono">
                                <span className="text-[9px] text-blue-600 font-medium">PRIMARY</span>
                                {typeof (indication as { icd10Codes: { leadingCondition: string | { code: string; name?: string } } }).icd10Codes.leadingCondition === 'object'
                                  ? (indication as { icd10Codes: { leadingCondition: { code: string; name?: string } } }).icd10Codes.leadingCondition.code
                                  : (indication as { icd10Codes: { leadingCondition: string } }).icd10Codes.leadingCondition}
                              </span>
                            )}
                            {((indication as { icd10Codes?: { otherConditions?: Array<string | { code: string; name?: string }> } }).icd10Codes?.otherConditions || []).map((code, i) => (
                              <span key={i} className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-mono">
                                {typeof code === 'object' ? code.code : code}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {(indication as { indicationAnalysis?: { reasoning?: string } }).indicationAnalysis?.reasoning && (
                        <div className="mb-3">
                          <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">Reasoning</span>
                          <p className="text-xs text-gray-600 leading-relaxed mt-1">
                            {(indication as { indicationAnalysis: { reasoning: string } }).indicationAnalysis.reasoning}
                          </p>
                        </div>
                      )}

                      {/* Criteria Adjustments */}
                      {((indication as { adjustedCriteria?: { addNew?: string[] } }).adjustedCriteria?.addNew?.length ?? 0) > 0 && (
                        <div className="mt-4 pt-3 border-t border-gray-100">
                          <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">Suggested Matching Criteria Adjustments</span>
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {(indication as { adjustedCriteria: { addNew: string[] } }).adjustedCriteria.addNew.map((adjustment, i) => (
                              <span
                                key={i}
                                className="px-2 py-1 text-xs bg-primary-50 text-primary-700 border border-primary-200 rounded"
                              >
                                + {adjustment}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Patient Expectations Tab */}
                  {activeAnalysisTab === 'expectations' && (indication as { patientExpectations?: { expectationAlignment?: string } }).patientExpectations && (
                    <div className="p-4 rounded-lg border border-gray-200 bg-white">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className={clsx(
                            'p-1.5 rounded-md',
                            (indication as { patientExpectations: { needsExpectationManagement?: boolean } }).patientExpectations.needsExpectationManagement
                              ? 'bg-amber-100'
                              : 'bg-green-100'
                          )}>
                            <Users className={clsx(
                              'h-4 w-4',
                              (indication as { patientExpectations: { needsExpectationManagement?: boolean } }).patientExpectations.needsExpectationManagement
                                ? 'text-amber-600'
                                : 'text-green-600'
                            )} />
                          </div>
                          <span className="text-sm font-medium text-gray-900">Patient Expectations</span>
                        </div>
                        <span
                          className={clsx(
                            'px-2 py-0.5 text-xs font-medium rounded capitalize',
                            (indication as { patientExpectations: { expectationAlignment: string } }).patientExpectations.expectationAlignment === 'realistic' && 'bg-green-100 text-green-700',
                            (indication as { patientExpectations: { expectationAlignment: string } }).patientExpectations.expectationAlignment === 'optimistic' && 'bg-yellow-100 text-yellow-700',
                            (indication as { patientExpectations: { expectationAlignment: string } }).patientExpectations.expectationAlignment === 'unrealistic' && 'bg-red-100 text-red-700',
                            (indication as { patientExpectations: { expectationAlignment: string } }).patientExpectations.expectationAlignment === 'unclear' && 'bg-gray-100 text-gray-700'
                          )}
                        >
                          {(indication as { patientExpectations: { expectationAlignment: string } }).patientExpectations.expectationAlignment}
                        </span>
                      </div>

                      {/* Communication Needs */}
                      {(indication as { patientExpectations: { communicationNeeds?: string } }).patientExpectations.communicationNeeds && (
                        <div className="mb-4">
                          <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">Communication Needs</span>
                          <p className="text-xs text-gray-600 leading-relaxed mt-1">
                            {(indication as { patientExpectations: { communicationNeeds: string } }).patientExpectations.communicationNeeds}
                          </p>
                        </div>
                      )}

                      {/* Numbered Expectations with Icons */}
                      {((indication as { patientExpectations: { providerTraits?: string[] } }).patientExpectations.providerTraits?.length ?? 0) > 0 && (
                        <div>
                          <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">
                            Ideal Provider Traits ({(indication as { patientExpectations: { providerTraits: string[] } }).patientExpectations.providerTraits.length})
                          </span>
                          <div className="space-y-2 mt-2">
                            {(indication as { patientExpectations: { providerTraits: string[] } }).patientExpectations.providerTraits.map((trait, i) => {
                              // Map traits to relevant icons
                              const icons = [Pill, Hospital, Briefcase, Timer, Users, Award];
                              const IconComponent = icons[i % icons.length];
                              return (
                                <div key={i} className="flex items-center gap-3 p-2 bg-gray-50 rounded-md">
                                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary-100 text-primary-700 text-xs font-bold">
                                    {i + 1}
                                  </div>
                                  <IconComponent className="h-4 w-4 text-gray-400" />
                                  <span className="text-xs text-gray-700">{trait}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Documents Tab */}
                  {activeAnalysisTab === 'documents' && (indication as { documentFindings?: { documentsReviewed?: boolean } }).documentFindings?.documentsReviewed && (
                    <div className={clsx(
                      "p-4 rounded-lg border bg-white",
                      (indication as { documentFindings?: { hasInconsistencies?: boolean } }).documentFindings?.hasInconsistencies
                        ? "border-red-200"
                        : "border-gray-200"
                    )}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className={clsx(
                            "p-1.5 rounded-md",
                            (indication as { documentFindings?: { hasInconsistencies?: boolean } }).documentFindings?.hasInconsistencies
                              ? "bg-red-100"
                              : "bg-purple-100"
                          )}>
                            <FileText className={clsx(
                              "h-4 w-4",
                              (indication as { documentFindings?: { hasInconsistencies?: boolean } }).documentFindings?.hasInconsistencies
                                ? "text-red-600"
                                : "text-purple-600"
                            )} />
                          </div>
                          <span className="text-sm font-medium text-gray-900">Document Findings</span>
                        </div>
                        {((indication as { documentFindings?: { keyFindings?: string[] } }).documentFindings?.keyFindings?.length ?? 0) > 0 && (
                          <span className={clsx(
                            "px-2 py-0.5 text-xs font-medium rounded",
                            (indication as { documentFindings?: { hasInconsistencies?: boolean } }).documentFindings?.hasInconsistencies
                              ? "bg-red-100 text-red-700"
                              : "bg-purple-100 text-purple-700"
                          )}>
                            {(indication as { documentFindings: { keyFindings: string[] } }).documentFindings.keyFindings.length} findings
                          </span>
                        )}
                      </div>

                      {/* Clinical Details Summary */}
                      {(indication as { documentFindings?: { clinicalDetails?: string } }).documentFindings?.clinicalDetails && (
                        <div className="mb-4">
                          <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">Clinical Summary</span>
                          <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                            {(indication as { documentFindings: { clinicalDetails: string } }).documentFindings.clinicalDetails}
                          </p>
                        </div>
                      )}

                      {/* Key Findings with Progressive Disclosure */}
                      {((indication as { documentFindings?: { keyFindings?: string[] } }).documentFindings?.keyFindings?.length ?? 0) > 0 && (
                        <div className="mb-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">Key Findings</span>
                            {(indication as { documentFindings: { keyFindings: string[] } }).documentFindings.keyFindings.length > 3 && (
                              <button
                                onClick={() => setShowAllFindings(!showAllFindings)}
                                className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                              >
                                {showAllFindings ? 'Show less' : `Show all ${(indication as { documentFindings: { keyFindings: string[] } }).documentFindings.keyFindings.length}`}
                              </button>
                            )}
                          </div>
                          <div className="space-y-1.5">
                            {(indication as { documentFindings: { keyFindings: string[] } }).documentFindings.keyFindings
                              .slice(0, showAllFindings ? undefined : 3)
                              .map((finding, i) => (
                                <div key={i} className="flex items-start gap-2 text-xs">
                                  <CheckCircle className="h-3.5 w-3.5 text-purple-500 mt-0.5 shrink-0" />
                                  <span className="text-gray-700">{finding}</span>
                                </div>
                              ))}
                            {!showAllFindings && (indication as { documentFindings: { keyFindings: string[] } }).documentFindings.keyFindings.length > 3 && (
                              <button
                                onClick={() => setShowAllFindings(true)}
                                className="text-xs text-gray-500 hover:text-gray-700 ml-5"
                              >
                                +{(indication as { documentFindings: { keyFindings: string[] } }).documentFindings.keyFindings.length - 3} more findings...
                              </button>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Additional Comorbidities */}
                      {((indication as { documentFindings?: { additionalComorbidities?: string[] } }).documentFindings?.additionalComorbidities?.length ?? 0) > 0 && (
                        <div className="mb-4">
                          <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">Additional Comorbidities</span>
                          <div className="flex flex-wrap gap-1.5 mt-1">
                            {(indication as { documentFindings: { additionalComorbidities: string[] } }).documentFindings.additionalComorbidities.map((item, i) => (
                              <span key={i} className="px-2 py-0.5 text-xs bg-purple-50 text-purple-700 border border-purple-200 rounded">
                                {item}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Inconsistencies (if any) */}
                      {((indication as { documentFindings?: { inconsistencies?: string[] } }).documentFindings?.inconsistencies?.length ?? 0) > 0 && (
                        <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                          <div className="flex items-center gap-2 mb-2">
                            <AlertTriangle className="h-4 w-4 text-amber-600" />
                            <span className="text-xs font-semibold text-amber-800 uppercase tracking-wide">
                              Inconsistencies Found
                            </span>
                          </div>
                          <div className="space-y-2">
                            {(indication as { documentFindings: { inconsistencies: string[] } }).documentFindings.inconsistencies.map((item, i) => (
                              <div key={i} className="text-xs text-amber-800 bg-white px-2.5 py-1.5 rounded border border-amber-200">
                                {item}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        )}

        {/* Step 5: Matching Criteria */}
        {currentStep === 'criteria' && (
          <div className={criteriaMutation.isPending ? "flex flex-col items-center justify-center py-12" : ""}>
            {/* Add Criteria Modal */}
            {addCriteriaModal && (
              <div className="fixed inset-0 z-50 flex items-center justify-center">
                {/* Backdrop */}
                <div
                  className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                  onClick={() => setAddCriteriaModal(null)}
                />
                {/* Modal */}
                <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
                  {/* Header */}
                  <div className={clsx(
                    'px-5 py-4 border-b',
                    addCriteriaModal.priority === 'critical' && 'bg-red-50 border-red-200',
                    addCriteriaModal.priority === 'high' && 'bg-orange-50 border-orange-200',
                    addCriteriaModal.priority === 'medium' && 'bg-yellow-50 border-yellow-200',
                    addCriteriaModal.priority === 'low' && 'bg-gray-50 border-gray-200',
                  )}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Plus className={clsx(
                          'h-5 w-5',
                          addCriteriaModal.priority === 'critical' && 'text-red-600',
                          addCriteriaModal.priority === 'high' && 'text-orange-600',
                          addCriteriaModal.priority === 'medium' && 'text-yellow-600',
                          addCriteriaModal.priority === 'low' && 'text-gray-600',
                        )} />
                        <h3 className="text-lg font-semibold text-gray-900">Add Criteria</h3>
                      </div>
                      <button
                        onClick={() => setAddCriteriaModal(null)}
                        className="p-1.5 rounded-lg hover:bg-white/50 text-gray-500 hover:text-gray-700"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>
                    <p className="mt-1 text-sm text-gray-600">
                      Adding to <span className={clsx(
                        'font-medium',
                        addCriteriaModal.priority === 'critical' && 'text-red-700',
                        addCriteriaModal.priority === 'high' && 'text-orange-700',
                        addCriteriaModal.priority === 'medium' && 'text-yellow-700',
                        addCriteriaModal.priority === 'low' && 'text-gray-700',
                      )}>
                        {addCriteriaModal.priority === 'critical' ? 'Critical' :
                         addCriteriaModal.priority === 'high' ? 'High Priority' :
                         addCriteriaModal.priority === 'medium' ? 'Medium Priority' : 'Low Priority'}
                      </span> criteria
                    </p>
                  </div>
                  {/* Form */}
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (!addCriteriaModal.name.trim()) return;

                      const criteriaId = `manual-${Date.now()}`;
                      setCriteria((prev) => {
                        if (!prev) return prev;
                        const criteriaData = prev as Record<string, unknown>;
                        const criteriaList = (criteriaData.criteria || criteriaData.search_criteria || criteriaData.matching_criteria || []) as Array<Record<string, unknown>>;
                        const newCriteria = {
                          name: addCriteriaModal.name.trim(),
                          description: addCriteriaModal.description.trim(),
                          importance: addCriteriaModal.priority,
                          _manualId: criteriaId,
                        };
                        const key = criteriaData.criteria ? 'criteria' : criteriaData.search_criteria ? 'search_criteria' : 'matching_criteria';
                        return { ...criteriaData, [key]: [...criteriaList, newCriteria] };
                      });
                      setManualCriteria(prev => new Set(prev).add(criteriaId));
                      setAddCriteriaModal(null);
                    }}
                    className="p-5 space-y-4"
                  >
                    <div>
                      <label htmlFor="criteria-name" className="block text-sm font-medium text-gray-700 mb-1">
                        Criteria Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="criteria-name"
                        type="text"
                        value={addCriteriaModal.name}
                        onChange={(e) => setAddCriteriaModal(prev => prev ? { ...prev, name: e.target.value } : null)}
                        placeholder="e.g., Experience with rare conditions"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                        autoFocus
                      />
                    </div>
                    <div>
                      <label htmlFor="criteria-description" className="block text-sm font-medium text-gray-700 mb-1">
                        Description <span className="text-gray-400">(optional)</span>
                      </label>
                      <textarea
                        id="criteria-description"
                        value={addCriteriaModal.description}
                        onChange={(e) => setAddCriteriaModal(prev => prev ? { ...prev, description: e.target.value } : null)}
                        placeholder="Additional details about this criteria..."
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm resize-none"
                      />
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                      <button
                        type="button"
                        onClick={() => setAddCriteriaModal(null)}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={!addCriteriaModal.name.trim()}
                        className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Add Criteria
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Delete Criteria Confirmation Modal */}
            {deleteCriteriaModal && (
              <div className="fixed inset-0 z-50 flex items-center justify-center">
                {/* Backdrop */}
                <div
                  className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                  onClick={() => setDeleteCriteriaModal(null)}
                />
                {/* Modal */}
                <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
                  {/* Header */}
                  <div className="px-5 py-4 bg-red-50 border-b border-red-200">
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                        <AlertTriangle className="h-5 w-5 text-red-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">Delete Criteria</h3>
                        <p className="text-sm text-gray-600">This action cannot be undone</p>
                      </div>
                    </div>
                  </div>
                  {/* Content */}
                  <div className="p-5">
                    <p className="text-sm text-gray-700">
                      Are you sure you want to delete the criteria:
                    </p>
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-sm font-medium text-gray-900">{deleteCriteriaModal.name}</p>
                      <p className="text-xs text-gray-500 mt-1 capitalize">
                        {deleteCriteriaModal.importance === 'critical' ? 'Critical' :
                         deleteCriteriaModal.importance === 'high' ? 'High Priority' :
                         deleteCriteriaModal.importance === 'medium' ? 'Medium Priority' : 'Low Priority'} criteria
                      </p>
                    </div>
                  </div>
                  {/* Actions */}
                  <div className="px-5 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-2">
                    <button
                      onClick={() => setDeleteCriteriaModal(null)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        // Perform the deletion
                        setCriteria((prev) => {
                          if (!prev) return prev;
                          const data = prev as Record<string, unknown>;
                          const list = (data.criteria || data.search_criteria || data.matching_criteria || []) as Array<Record<string, unknown>>;

                          // Find the actual index in the flat list
                          let count = 0;
                          let targetIndex = -1;
                          for (let i = 0; i < list.length; i++) {
                            const imp = (list[i].importance || list[i].priority || list[i].weight || 'medium') as string;
                            if (imp === deleteCriteriaModal.importance) {
                              if (count === deleteCriteriaModal.index) {
                                targetIndex = i;
                                break;
                              }
                              count++;
                            }
                          }

                          if (targetIndex === -1) return prev;

                          const newList = [...list];
                          newList.splice(targetIndex, 1);
                          const key = data.criteria ? 'criteria' : data.search_criteria ? 'search_criteria' : 'matching_criteria';
                          return { ...data, [key]: newList };
                        });

                        // Remove from manualCriteria set
                        setManualCriteria(prev => {
                          const next = new Set(prev);
                          next.delete(deleteCriteriaModal.manualId);
                          return next;
                        });

                        setDeleteCriteriaModal(null);
                      }}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                    >
                      Delete Criteria
                    </button>
                  </div>
                </div>
              </div>
            )}

            {criteriaMutation.isPending ? (
              <>
                <div className="relative">
                  <div className="h-16 w-16 rounded-full bg-primary-100 animate-pulse" />
                  <Loader2 className="absolute inset-0 m-auto h-8 w-8 text-primary-600 animate-spin" />
                </div>
                <h2 className="mt-4 text-lg font-semibold text-gray-900">Building Matching Criteria...</h2>
                <p className="mt-1 text-sm text-gray-500">
                  Defining the specialty, qualifications, and location requirements to find the most suitable healthcare providers
                </p>
              </>
            ) : criteria ? (
              <div className="w-full">
                <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-100">
                  <div>
                    <h2 className="text-sm font-medium text-gray-900">Matching Criteria</h2>
                    <p className="text-xs text-gray-500 mt-0.5">Toggle criteria to refine care provider matching</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => criteriaMutation.mutate()}
                      disabled={criteriaMutation.isPending}
                      className="px-2.5 py-1 text-xs bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-md font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                    >
                      {criteriaMutation.isPending ? (
                        <>
                          <Loader2 className="w-3 h-3 animate-spin" />
                          Regenerating...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="w-3 h-3" />
                          Regenerate
                        </>
                      )}
                    </button>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <CheckCircle className="h-3.5 w-3.5 text-green-500" />
                      {(() => {
                        const criteriaData = criteria as Record<string, unknown>;
                        const criteriaList = (criteriaData.criteria || criteriaData.search_criteria || criteriaData.matching_criteria || []) as Array<Record<string, unknown>>;
                        const totalCount = criteriaList.length;
                        const activeCount = totalCount - disabledCriteria.size;
                        return `${activeCount}/${totalCount} criteria active`;
                      })()}
                    </div>
                  </div>
                </div>

                {/* Group criteria by importance */}
                {(() => {
                  type CriteriaItem = Record<string, unknown>;
                  type GroupedCriteria = Record<string, CriteriaItem[]>;

                  const criteriaData = criteria as Record<string, unknown>;
                  const criteriaList = (criteriaData.criteria || criteriaData.search_criteria || criteriaData.matching_criteria || []) as CriteriaItem[];

                  // Group by importance
                  const grouped: GroupedCriteria = criteriaList.reduce<GroupedCriteria>((acc, c) => {
                    const importance = (c.importance || c.priority || c.weight || 'medium') as string;
                    if (!acc[importance]) acc[importance] = [];
                    acc[importance].push(c);
                    return acc;
                  }, {});

                  const importanceOrder = ['critical', 'high', 'medium', 'low'];
                  const importanceConfig = {
                    critical: { bg: 'bg-red-50', border: 'border-red-200', badge: 'bg-red-100 text-red-700', icon: 'text-red-500', label: 'Critical', addBtn: 'bg-red-100 text-red-700 hover:bg-red-200' },
                    high: { bg: 'bg-orange-50', border: 'border-orange-200', badge: 'bg-orange-100 text-orange-700', icon: 'text-orange-500', label: 'High Priority', addBtn: 'bg-orange-100 text-orange-700 hover:bg-orange-200' },
                    medium: { bg: 'bg-yellow-50', border: 'border-yellow-200', badge: 'bg-yellow-100 text-yellow-700', icon: 'text-yellow-500', label: 'Medium Priority', addBtn: 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200' },
                    low: { bg: 'bg-gray-50', border: 'border-gray-200', badge: 'bg-gray-100 text-gray-600', icon: 'text-gray-400', label: 'Low Priority', addBtn: 'bg-gray-200 text-gray-700 hover:bg-gray-300' },
                  };

                  return (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                      {importanceOrder.map((importance) => {
                        const items = grouped[importance] || [];
                        const config = importanceConfig[importance as keyof typeof importanceConfig];

                        return (
                          <div key={importance} className={`rounded-lg border ${config.border} ${config.bg} p-3`}>
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.badge}`}>
                                  {config.label}
                                </span>
                                {items.length > 0 && (
                                  <span className="text-xs text-gray-500">({items.length})</span>
                                )}
                              </div>
                              <button
                                onClick={() => setAddCriteriaModal({ priority: importance, name: '', description: '' })}
                                className={`px-2 py-1 text-xs rounded-md font-medium flex items-center gap-1 ${config.addBtn}`}
                              >
                                <Plus className="w-3 h-3" />
                                Add
                              </button>
                            </div>
                            {items.length === 0 ? (
                              <div className="text-center py-4 text-xs text-gray-500">
                                No {config.label.toLowerCase()} criteria yet
                              </div>
                            ) : (
                              <div className="space-y-2">
                                {items.map((c: CriteriaItem, i: number) => {
                                  const name = (c.name || c.criterion || c.title || c.category || '') as string;
                                  const description = (c.description || c.details || c.explanation || c.rationale || '') as string;
                                  const criteriaKey = `${importance}-${i}`;
                                  const isDisabled = disabledCriteria.has(criteriaKey);
                                  const isManual = Boolean(c._manualId) && manualCriteria.has(c._manualId as string);

                                  return (
                                    <div
                                      key={i}
                                      className={clsx(
                                        'flex items-start gap-2 rounded p-2 border transition-all',
                                        isDisabled
                                          ? 'bg-gray-50 border-gray-200 opacity-60'
                                          : 'bg-white border-gray-100 hover:border-gray-200'
                                      )}
                                    >
                                      <label className="flex items-start gap-2 flex-1 cursor-pointer">
                                        <input
                                          type="checkbox"
                                          checked={!isDisabled}
                                          onChange={() => {
                                            setDisabledCriteria(prev => {
                                              const next = new Set(prev);
                                              if (next.has(criteriaKey)) {
                                                next.delete(criteriaKey);
                                              } else {
                                                next.add(criteriaKey);
                                              }
                                              return next;
                                            });
                                          }}
                                          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                        />
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center gap-1.5">
                                            <p className={clsx(
                                              'text-sm font-medium',
                                              isDisabled ? 'text-gray-500 line-through' : 'text-gray-900'
                                            )}>
                                              {name}
                                            </p>
                                            {isManual && (
                                              <span className="px-1.5 py-0.5 text-[10px] font-medium bg-blue-100 text-blue-700 rounded">
                                                Manual
                                              </span>
                                            )}
                                          </div>
                                          {description && (
                                            <p className={clsx(
                                              'text-xs mt-0.5 line-clamp-2',
                                              isDisabled ? 'text-gray-400' : 'text-gray-600'
                                            )}>
                                              {description}
                                            </p>
                                          )}
                                        </div>
                                      </label>
                                      {isManual && (
                                        <button
                                          onClick={() => {
                                            setDeleteCriteriaModal({
                                              importance,
                                              index: i,
                                              name,
                                              manualId: c._manualId as string,
                                            });
                                          }}
                                          className="p-1 rounded hover:bg-red-100 text-gray-400 hover:text-red-600 transition-colors"
                                          title="Delete criteria"
                                        >
                                          <X className="w-3.5 h-3.5" />
                                        </button>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            ) : null}
          </div>
        )}

        {/* Step 6: Matching to Care Provider */}
        {currentStep === 'recommendations' && (
          <div className="w-full">
            <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-100">
              <h2 className="text-sm font-medium text-gray-900">Matching to Care Provider</h2>
              <div className="flex items-center gap-2">
                {/* Send to Care Providers button */}
                <button
                  onClick={() => {
                    // TODO: Implement send to care providers functionality
                    alert('Send to Care Providers feature coming soon');
                  }}
                  className="px-2.5 py-1 text-xs bg-primary-100 text-primary-700 hover:bg-primary-200 rounded-md font-medium flex items-center gap-1.5"
                >
                  <Send className="w-3 h-3" />
                  Send to Providers
                </button>
                {/* Regenerate criteria button */}
                <button
                  onClick={() => {
                    setCurrentStep('criteria');
                    criteriaMutation.mutate();
                  }}
                  disabled={criteriaMutation.isPending}
                  className="px-2.5 py-1 text-xs bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-md font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                  title="Regenerate search criteria with updated specialty mapping"
                >
                  {criteriaMutation.isPending ? (
                    <>
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Regenerating...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-3 h-3" />
                      Regen Criteria
                    </>
                  )}
                </button>
                {/* Sort dropdown */}
                <div className="flex items-center gap-1.5">
                  <ArrowUpDown className="h-3.5 w-3.5 text-gray-400" />
                  <select
                    value={doctorSortBy}
                    onChange={(e) => setDoctorSortBy(e.target.value as 'match' | 'name' | 'city')}
                    className="text-xs border-gray-200 rounded py-1 pl-2 pr-6 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="match">Sort by Match %</option>
                    <option value="name">Sort by Name</option>
                    <option value="city">Sort by City</option>
                  </select>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  {isAnySearchPending ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 text-primary-500 animate-spin" />
                      Matching...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-3.5 w-3.5 text-green-500" />
                      {recommendations.claude.length + recommendations.gemini.length + recommendations.chatgpt.length} matches found
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Source tabs */}
            <div className="flex gap-1 mb-3 p-0.5 bg-gray-100 rounded-lg w-fit">
              {([
                { key: 'claude' as const, label: 'Claude', badge: 'C', color: 'violet', mutation: claudeSearchMutation },
                { key: 'gemini' as const, label: 'Gemini', badge: 'G', color: 'green', mutation: geminiSearchMutation },
                { key: 'chatgpt' as const, label: 'ChatGPT', badge: 'O', color: 'teal', mutation: chatgptSearchMutation },
              ]).map(tab => {
                const isActive = activeRecommendationTab === tab.key;
                const isPending = tab.mutation.isPending;
                const hasError = searchErrors[tab.key].hasError;
                const count = recommendations[tab.key].length;
                const bgActive = tab.color === 'violet' ? 'bg-violet-600' : tab.color === 'green' ? 'bg-green-600' : 'bg-teal-600';
                const bgBadge = tab.color === 'violet' ? 'bg-violet-500' : tab.color === 'green' ? 'bg-green-500' : 'bg-teal-500';
                return (
                  <button
                    key={tab.key}
                    onClick={() => setActiveRecommendationTab(tab.key)}
                    className={clsx(
                      'px-3 py-1.5 text-xs font-medium rounded flex items-center gap-1.5 transition-all',
                      isActive ? `${bgActive} text-white shadow-sm` : 'text-gray-600 hover:bg-gray-200'
                    )}
                  >
                    {isPending ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <span className={clsx('w-3.5 h-3.5 flex items-center justify-center text-[9px] font-bold rounded', isActive ? `${bgBadge} text-white` : 'bg-gray-300 text-white')}>{tab.badge}</span>
                    )}
                    {tab.label}
                    {!isPending && (
                      hasError ? <span className={clsx('text-[10px]', isActive ? 'text-red-300' : 'text-red-400')}>(error)</span>
                      : count === 0 ? <span className="text-[10px] opacity-60">(0)</span>
                      : <span className={clsx('px-1.5 rounded text-[10px]', isActive ? 'bg-white/20' : 'bg-gray-200 text-gray-700')}>{count}</span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Doctor Cards Grid — renders active tab */}
            {(() => {
              const activeTab = activeRecommendationTab;
              const activeMutation = activeTab === 'claude' ? claudeSearchMutation : activeTab === 'chatgpt' ? chatgptSearchMutation : geminiSearchMutation;
              const activeResults = recommendations[activeTab];
              const activeError = searchErrors[activeTab];
              const tabLabel = activeTab === 'claude' ? 'Claude' : activeTab === 'chatgpt' ? 'ChatGPT' : 'Gemini';
              const tabColor = activeTab === 'claude' ? 'violet' : activeTab === 'chatgpt' ? 'teal' : 'green';

              return (
                <div>
                  {activeMutation.isPending ? (
                    <div className="flex flex-col items-center justify-center py-12 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="relative">
                        <div className={clsx('h-12 w-12 rounded-full animate-pulse', `bg-${tabColor}-100`)} />
                        <Loader2 className={clsx('absolute inset-0 m-auto h-6 w-6 animate-spin', `text-${tabColor}-600`)} />
                      </div>
                      <p className="mt-3 text-sm font-medium text-gray-700">{tabLabel} is matching care providers...</p>
                      <p className="mt-1 text-xs text-gray-500">Matching may take a moment</p>
                    </div>
                  ) : activeResults.length === 0 ? (
                    <div className={clsx(
                      "text-center py-8 rounded-lg border",
                      activeError.hasError ? "bg-red-50 border-red-200" : "bg-gray-50 border-gray-200"
                    )}>
                      {activeError.hasError ? (
                        <>
                          <AlertTriangle className="mx-auto h-8 w-8 text-red-400 mb-2" />
                          <p className="text-sm font-medium text-red-700">Search error from {tabLabel}</p>
                          <p className="text-xs mt-1 max-w-sm mx-auto text-red-600">
                            {activeError.errorType === 'parse_error'
                              ? 'The search completed but the results could not be processed. This is a temporary issue.'
                              : activeError.message || 'An error occurred while searching. Please try again.'}
                          </p>
                          <div className="mt-4 pt-4 border-t border-red-200">
                            <button
                              onClick={() => {
                                setSearchErrors(prev => ({ ...prev, [activeTab]: { hasError: false } }));
                                activeMutation.mutate();
                              }}
                              disabled={activeMutation.isPending}
                              className="px-4 py-2 text-sm bg-red-500 text-white hover:bg-red-600 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2 shadow-sm"
                            >
                              {activeMutation.isPending ? (
                                <><Loader2 className="w-4 h-4 animate-spin" /> Retrying...</>
                              ) : (
                                <><RefreshCw className="w-4 h-4" /> Retry Search</>
                              )}
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <Users className="mx-auto h-8 w-8 text-gray-300 mb-2" />
                          <p className="text-sm font-medium text-gray-700">No care providers found</p>
                          <p className="text-xs mt-1 max-w-sm mx-auto">
                            {tabLabel} did not find matching care providers. Try another tab or adjust your criteria.
                          </p>
                        </>
                      )}
                    </div>
                  ) : (
                    <div>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                      {[...activeResults]
                        .sort((a, b) => {
                          switch (doctorSortBy) {
                            case 'name':
                              return a.name.localeCompare(b.name);
                            case 'city':
                              return (a.city || '').localeCompare(b.city || '');
                            case 'match':
                            default:
                              return b.matchScore - a.matchScore;
                          }
                        })
                        .map((doc, idx) => {
                          const rankBg = tabColor === 'violet' ? 'bg-violet-100 text-violet-700' : tabColor === 'teal' ? 'bg-teal-100 text-teal-700' : 'bg-green-100 text-green-700';
                          const criteriaBg = tabColor === 'violet' ? 'bg-violet-50 text-violet-700' : tabColor === 'teal' ? 'bg-teal-50 text-teal-700' : 'bg-green-50 text-green-700';
                          return (
                        <button
                          key={doc.doctorId}
                          onClick={() => setSelectedDoctor(doc)}
                          className="p-3 rounded-lg bg-white border border-gray-200 hover:border-primary-300 hover:shadow-sm transition-all text-left w-full"
                        >
                          <div className="flex items-start gap-2">
                            <div className={clsx('w-7 h-7 rounded flex items-center justify-center text-xs font-bold flex-shrink-0', rankBg)}>
                              {idx + 1}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <h3 className="text-sm font-medium text-gray-900 truncate">
                                  {doc.title && `${doc.title} `}{doc.name}
                                </h3>
                                <span
                                  className={clsx(
                                    'px-1.5 py-0.5 text-[10px] font-bold rounded flex-shrink-0',
                                    doc.matchScore >= 90 ? 'bg-green-100 text-green-700' :
                                    doc.matchScore >= 75 ? 'bg-blue-100 text-blue-700' :
                                    doc.matchScore >= 60 ? 'bg-yellow-100 text-yellow-700' :
                                    'bg-gray-100 text-gray-700'
                                  )}
                                >
                                  {doc.matchScore}%
                                </span>
                              </div>
                              <p className="text-xs text-gray-500">{doc.specialty}</p>
                              {(doc.city || doc.institution) && (
                                <p className="text-xs text-gray-400 mt-0.5">
                                  {doc.institution}{doc.institution && doc.city ? ' • ' : ''}{doc.city}{doc.canton ? `, ${doc.canton}` : ''}
                                </p>
                              )}
                              {doc.matchReasons && doc.matchReasons.length > 0 && (
                                <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                                  {doc.matchReasons.slice(0, 2).join('. ')}
                                </p>
                              )}
                              {doc.criteriaMatched && doc.criteriaMatched.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {doc.criteriaMatched.slice(0, 2).map((criterion, i) => (
                                    <span
                                      key={i}
                                      className={clsx('px-1.5 py-0.5 text-[10px] rounded', criteriaBg)}
                                    >
                                      {criterion}
                                    </span>
                                  ))}
                                  {doc.criteriaMatched.length > 2 && (
                                    <span className="px-1.5 py-0.5 text-[10px] bg-gray-100 text-gray-600 rounded">
                                      +{doc.criteriaMatched.length - 2}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* Step 7: Save/Complete Case */}
        {currentStep === 'results' && (
          <div className="py-8">
            {savedCaseId ? (
              <div className="text-center animate-in">
                <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-primary-100">
                  <Check className="h-10 w-10 text-primary-600" />
                </div>
                <h2 className="mt-6 text-xl font-semibold font-display text-navy">
                  Case Analysis Completed
                </h2>
                <p className="mt-2 text-gray-500 max-w-md mx-auto">
                  The analysis is complete with {recommendations.gemini.length} matched care providers ready to share with the patient.
                </p>
                <div className="mt-8 flex flex-col items-center gap-3">
                  <button
                    onClick={() => setShowSendToPatientModal(true)}
                    className="btn-primary flex items-center gap-2"
                  >
                    <Send className="h-4 w-4" />
                    Send to Patient
                  </button>
                  <div className="flex gap-3">
                    <button
                      onClick={() => navigate(`/cases/${savedCaseId}`)}
                      className="btn-secondary"
                    >
                      View Case Details
                    </button>
                    <button
                      onClick={() => navigate('/cases')}
                      className="btn-outline"
                    >
                      View All Cases
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="max-w-lg mx-auto">
                <div className="text-center mb-8">
                  <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary-100">
                    <Save className="h-8 w-8 text-primary-600" />
                  </div>
                  <h2 className="mt-4 text-xl font-semibold font-display text-navy">Analysis Complete</h2>
                  <p className="mt-2 text-gray-500">
                    {isExistingCase
                      ? 'Save the analysis results to update this case.'
                      : 'Save this analysis as a case for future reference and care provider matching.'}
                  </p>
                </div>

                <div className="space-y-4">
                  {/* Only show title input for standalone mode */}
                  {!isExistingCase && (
                    <div>
                      <label className="form-label">Case Title (optional)</label>
                      <input
                        type="text"
                        value={caseTitle}
                        onChange={(e) => setCaseTitle(e.target.value)}
                        className="input"
                        placeholder={`${specialistRole || 'Medical'} Case - ${new Date().toLocaleDateString()}`}
                      />
                    </div>
                  )}

                  <div className="rounded-xl bg-gray-50 p-4 space-y-2">
                    <h3 className="text-sm font-medium text-navy">Summary</h3>
                    <div className="text-sm text-gray-600 space-y-1">
                      {isExistingCase && existingCaseTitle && (
                        <p>• Case: {existingCaseTitle}</p>
                      )}
                      <p>• Patient: {patientInfo.age ? `${patientInfo.age} years old` : 'Age not specified'}, {patientInfo.gender || 'Gender not specified'}</p>
                      <p>• Location: {patientInfo.canton ? SWISS_CANTONS.find(c => c.code === patientInfo.canton)?.name || patientInfo.canton : 'Canton not specified'}</p>
                      <p>• Questions answered: {Object.keys(answers).length} / {questions.length}</p>
                      <p>• Match criteria: {(criteria as { criteria: Array<unknown> })?.criteria?.length || 0} criteria generated</p>
                      {recommendations.gemini.length > 0 && (
                        <p>• Care providers found: {recommendations.gemini.length} matches (Gemini Search)</p>
                      )}
                    </div>
                  </div>

                  <button
                    onClick={handleSaveAndComplete}
                    disabled={saveCaseMutation.isPending}
                    className="btn-primary w-full"
                  >
                    {saveCaseMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {isExistingCase ? 'Updating Case...' : 'Saving Case...'}
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        {isExistingCase ? 'Update Case' : 'Save Case'}
                      </>
                    )}
                  </button>

                  {saveCaseMutation.isError && (
                    <p className="text-sm text-red-600 text-center">
                      Error saving case: {saveCaseMutation.error?.message || String(saveCaseMutation.error)}
                    </p>
                  )}

                  {/* Send to Patient - available even before saving */}
                  <div className="pt-4 border-t border-gray-200">
                    <button
                      onClick={() => setShowSendToPatientModal(true)}
                      className="btn-outline w-full flex items-center justify-center gap-2"
                    >
                      <Mail className="h-4 w-4" />
                      Send Results to Patient
                    </button>
                    <p className="text-xs text-gray-500 text-center mt-2">
                      Share the care provider recommendations via email or link
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Doctor Detail Modal */}
      {selectedDoctor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setSelectedDoctor(null)}>
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header - Improved with score ring */}
            <div className="relative p-5 border-b border-gray-100">
              {/* Close button */}
              <button
                onClick={() => setSelectedDoctor(null)}
                className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>

              <div className="flex items-start gap-4 pr-10">
                {/* Score Ring */}
                <div className="relative flex-shrink-0">
                  <svg className="w-16 h-16 -rotate-90" viewBox="0 0 36 36">
                    <circle
                      cx="18" cy="18" r="15.5"
                      fill="none"
                      stroke="#e5e7eb"
                      strokeWidth="3"
                    />
                    <circle
                      cx="18" cy="18" r="15.5"
                      fill="none"
                      stroke={selectedDoctor.matchScore >= 90 ? '#22c55e' : selectedDoctor.matchScore >= 75 ? '#3b82f6' : selectedDoctor.matchScore >= 60 ? '#eab308' : '#9ca3af'}
                      strokeWidth="3"
                      strokeDasharray={`${selectedDoctor.matchScore} 100`}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className={clsx(
                      'text-lg font-bold',
                      selectedDoctor.matchScore >= 90 ? 'text-green-600' :
                      selectedDoctor.matchScore >= 75 ? 'text-blue-600' :
                      selectedDoctor.matchScore >= 60 ? 'text-yellow-600' :
                      'text-gray-600'
                    )}>
                      {selectedDoctor.matchScore}
                    </span>
                  </div>
                </div>

                {/* Doctor Info */}
                <div className="flex-1 min-w-0">
                  <h2 className="text-xl font-semibold text-gray-900 leading-tight">
                    {selectedDoctor.title && <span className="text-gray-500">{selectedDoctor.title} </span>}
                    {selectedDoctor.name}
                  </h2>
                  <p className="text-sm text-primary-600 font-medium mt-0.5">{selectedDoctor.specialty}</p>
                  {selectedDoctor.institution && (
                    <div className="flex items-center gap-1.5 mt-2 text-sm text-gray-600">
                      <Building2 className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <span className="truncate">{selectedDoctor.institution}</span>
                    </div>
                  )}
                  {(selectedDoctor.city || selectedDoctor.canton) && (
                    <div className="flex items-center gap-1.5 mt-1 text-sm text-gray-500">
                      <MapPin className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <span>{selectedDoctor.city}{selectedDoctor.city && selectedDoctor.canton ? ', ' : ''}{selectedDoctor.canton}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Contact Information - if available */}
              {(selectedDoctor.address || selectedDoctor.phone || selectedDoctor.email) && (
                <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {selectedDoctor.address && (
                    <div className="flex items-start gap-2 text-sm">
                      <MapPin className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-600">{selectedDoctor.address}</span>
                    </div>
                  )}
                  {selectedDoctor.phone && (
                    <a href={`tel:${selectedDoctor.phone}`} className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700">
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      <span>{selectedDoctor.phone}</span>
                    </a>
                  )}
                  {selectedDoctor.email && (
                    <a href={`mailto:${selectedDoctor.email}`} className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700">
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <span>{selectedDoctor.email}</span>
                    </a>
                  )}
                </div>
              )}
            </div>

            {/* Modal Body */}
            <div className="p-5 overflow-y-auto max-h-[calc(90vh-200px)] space-y-4">
              {/* Match Reasons */}
              {selectedDoctor.matchReasons && selectedDoctor.matchReasons.length > 0 && (
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="p-1.5 rounded-lg bg-blue-100">
                      <Award className="h-4 w-4 text-blue-600" />
                    </div>
                    <span className="text-sm font-semibold text-blue-900">Why This Doctor Matches</span>
                  </div>
                  <ul className="space-y-2">
                    {selectedDoctor.matchReasons.map((reason, i) => (
                      <li key={i} className="text-sm text-blue-800 flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-blue-500" />
                        <span>{reason}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Matching Criteria Matched */}
              {selectedDoctor.criteriaMatched && selectedDoctor.criteriaMatched.length > 0 && (
                <div>
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Matching Criteria Matched</span>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {selectedDoctor.criteriaMatched.map((criterion, i) => (
                      <span
                        key={i}
                        className="px-3 py-1.5 text-xs bg-green-100 text-green-700 rounded-full font-medium border border-green-200"
                      >
                        {criterion}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Expertise */}
              {selectedDoctor.expertise && (
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Expertise</span>
                  <p className="mt-2 text-sm text-gray-700 leading-relaxed">{selectedDoctor.expertise}</p>
                </div>
              )}

              {/* Pros & Cons (ChatGPT specific) */}
              {(selectedDoctor.pros?.length || selectedDoctor.cons?.length) ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {selectedDoctor.pros && selectedDoctor.pros.length > 0 && (
                    <div className="bg-green-50 rounded-xl p-4 border border-green-100">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="p-1 rounded-md bg-green-100">
                          <ThumbsUp className="h-3.5 w-3.5 text-green-600" />
                        </div>
                        <span className="text-sm font-semibold text-green-800">Advantages</span>
                      </div>
                      <ul className="space-y-1.5">
                        {selectedDoctor.pros.map((pro, i) => (
                          <li key={i} className="text-sm text-green-700 flex items-start gap-2">
                            <span className="text-green-400 mt-1">•</span>
                            <span>{pro}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {selectedDoctor.cons && selectedDoctor.cons.length > 0 && (
                    <div className="bg-amber-50 rounded-xl p-4 border border-amber-100">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="p-1 rounded-md bg-amber-100">
                          <ThumbsDown className="h-3.5 w-3.5 text-amber-600" />
                        </div>
                        <span className="text-sm font-semibold text-amber-800">Considerations</span>
                      </div>
                      <ul className="space-y-1.5">
                        {selectedDoctor.cons.map((con, i) => (
                          <li key={i} className="text-sm text-amber-700 flex items-start gap-2">
                            <span className="text-amber-400 mt-1">•</span>
                            <span>{con}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : null}
            </div>

            {/* Modal Footer - Website link and source */}
            <div className="px-5 py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-400">Source:</span>
                <span
                  className={clsx(
                    'inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full',
                    'bg-green-100 text-green-700'
                  )}
                >
                  Gemini Search
                </span>
              </div>
              {selectedDoctor.website && (
                <a
                  href={selectedDoctor.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium shadow-sm"
                >
                  <ExternalLink className="h-4 w-4" />
                  Visit Website
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Document Preview Modal */}
      {previewDocument && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <div className="flex items-center gap-2">
                <span className="text-lg">
                  {previewDocument.mimeType.startsWith('image/') ? '🖼️' : previewDocument.mimeType === 'application/pdf' ? '📄' : '📎'}
                </span>
                <h3 className="text-lg font-semibold text-gray-900 truncate">
                  {previewDocument.originalName}
                </h3>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => downloadDocument(previewDocument.url, previewDocument.originalName)}
                  className="btn-outline text-sm"
                >
                  <Download className="h-4 w-4 mr-1" />
                  Download
                </button>
                <button
                  onClick={() => setPreviewDocument(null)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto p-4 bg-gray-50">
              {previewDocument.mimeType.startsWith('image/') ? (
                <AuthenticatedImage
                  src={getDocumentUrl(previewDocument.url)}
                  alt={previewDocument.originalName}
                  className="max-w-full h-auto mx-auto"
                />
              ) : previewDocument.mimeType === 'application/pdf' ? (
                <AuthenticatedPdfViewer
                  src={getDocumentUrl(previewDocument.url)}
                  title={previewDocument.originalName}
                  className="w-full h-[70vh]"
                />
              ) : (
                <div className="text-center py-12">
                  <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 mb-4">
                    Preview not available for this file type
                  </p>
                  <button
                    onClick={() => downloadDocument(previewDocument.url, previewDocument.originalName)}
                    className="btn-primary"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download File
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Send to Patient Modal */}
      {showSendToPatientModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-in fade-in">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-primary-50 to-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100">
                    <Send className="h-5 w-5 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-navy">Send to Patient</h3>
                    <p className="text-sm text-gray-500">Share questionnaire or results</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowSendToPatientModal(false);
                    setPatientEmail('');
                    setPatientMessage('');
                    setLinkCopied(false);
                  }}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="h-5 w-5 text-gray-400" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-5">
              {/* Copy Link Section */}
              <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Link className="h-4 w-4 text-gray-500" />
                    <span className="text-sm font-medium text-gray-700">Shareable Link</span>
                  </div>
                  <button
                    onClick={() => {
                      const link = `${window.location.origin}/patient-form/${savedCaseId || caseId || 'preview'}`;
                      navigator.clipboard.writeText(link);
                      setLinkCopied(true);
                      setTimeout(() => setLinkCopied(false), 2000);
                    }}
                    className={clsx(
                      'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all',
                      linkCopied
                        ? 'bg-green-100 text-green-700'
                        : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
                    )}
                  >
                    {linkCopied ? (
                      <>
                        <Check className="h-4 w-4" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4" />
                        Copy Link
                      </>
                    )}
                  </button>
                </div>
                <p className="text-xs text-gray-500">
                  Share this link with the patient to let them fill out the questionnaire directly.
                </p>
              </div>

              {/* Divider */}
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-gray-200" />
                <span className="text-xs text-gray-400 uppercase tracking-wide">or send via email</span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>

              {/* Email Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Patient Email
                </label>
                <input
                  type="email"
                  value={patientEmail}
                  onChange={(e) => setPatientEmail(e.target.value)}
                  placeholder="patient@example.com"
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-100 transition-all"
                />
              </div>

              {/* Optional Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Personal Message <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea
                  value={patientMessage}
                  onChange={(e) => setPatientMessage(e.target.value)}
                  placeholder="Add a personal note to the patient..."
                  rows={3}
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setShowSendToPatientModal(false);
                  setPatientEmail('');
                  setPatientMessage('');
                  setLinkCopied(false);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!patientEmail.trim()) return;
                  setSendingToPatient(true);
                  // Simulate sending - in production this would call an API
                  await new Promise(resolve => setTimeout(resolve, 1500));
                  setSendingToPatient(false);
                  setShowSendToPatientModal(false);
                  setPatientEmail('');
                  setPatientMessage('');
                  // Show success toast or notification
                  alert(`Questionnaire sent to ${patientEmail}`);
                }}
                disabled={!patientEmail.trim() || sendingToPatient}
                className={clsx(
                  'flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium transition-all',
                  patientEmail.trim() && !sendingToPatient
                    ? 'bg-primary-600 text-white hover:bg-primary-700'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                )}
              >
                {sendingToPatient ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="h-4 w-4" />
                    Send Email
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Local File Preview Modal (for uploaded files not yet saved) */}
      {previewLocalFile && (
        <LocalFilePreviewModal
          file={previewLocalFile.file}
          onClose={() => setPreviewLocalFile(null)}
        />
      )}
    </div>
  );
}

/**
 * Modal component for previewing local files (File objects, not yet uploaded)
 * Uses URL.createObjectURL for images and PDFs
 */
function LocalFilePreviewModal({ file, onClose }: { file: File; onClose: () => void }) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    setBlobUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const isImage = file.type.startsWith('image/');
  const isPdf = file.type === 'application/pdf';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <span className="text-lg">
              {isImage ? '🖼️' : isPdf ? '📄' : '📎'}
            </span>
            <h3 className="text-lg font-semibold text-gray-900 truncate">
              {file.name}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-auto p-4 bg-gray-50">
          {isImage && blobUrl ? (
            <img
              src={blobUrl}
              alt={file.name}
              className="max-w-full h-auto mx-auto"
            />
          ) : isPdf && blobUrl ? (
            <iframe
              src={blobUrl}
              className="w-full h-[70vh]"
              title={file.name}
            />
          ) : (
            <div className="text-center py-12">
              <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">
                Preview not available for this file type
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
